import React, { useState, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API_BASE_URL as API_BASE } from "../../config/apiConfig";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
    Home, MapPin, Ruler, Mountain,
    CheckCircle, Hash, Download, Copy, Info,
    AlertCircle, Maximize2, Activity, Map, Share2,
    Shield, Clock, Users, Layers, Sparkles, Edit2, Save, ArrowUpRight, X
} from "lucide-react";
import { clearCurrentAnalysis } from "../../redux/slices/analysisSlice";
import { exportAnalysisToPDF, getStoredMapSnapshot } from "../../utils/ProfessionalPDFExport";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import MapModal from "../Modal/MapModal";
import { cleanAddress, getAddressParts } from '../../utils/addressUtils';
import SharePropertyModal from "../Modal/SharePropertyModal";
import LockedOverlay from "../UI/LockedOverlay";
import CircularGauge from "../UI/CircularGauge";
import TimelineSparkline from "../UI/TimelineSparkline";
import ProjectScopingTool from "../UI/GfaTargetSlider";
import Tooltip from "../UI/Tooltip";
import {
    detectJurisdiction, getZoningMetric, getJurisdictionInfo,
} from "../Modal/MapModal/utils/jurisdictionHelpers";
import { updateProposedScoping } from "../../redux/slices/analysisSlice";

// ── Tab definitions ─────────────────────────────────────────────────────────
const TABS = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "terrain", label: "Terrain", icon: Mountain },
    { id: "contour", label: "Contour", icon: Ruler },
    { id: "zoning", label: "Zoning", icon: Layers },
    { id: "adu", label: "ADU", icon: Home },
    { id: "compliance", label: "Compliance", icon: Shield },
    { id: "reports", label: "Reports", icon: Clock },
    { id: "map", label: "Map", icon: Map },
];

// ── Stat row helper ─────────────────────────────────────────────────────────
const StatRow = ({ label, value, highlight = false, suffix = "", subValue = null }) => (
    <div className={`flex justify-between items-center py-3.5 border-b border-slate-100 last:border-0 transition-all duration-300 hover:translate-x-1 ${highlight ? "bg-indigo-50/50 px-4 -mx-1 rounded-xl" : ""}`}>
        <div className="flex flex-col">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest leading-none">{label}</span>
            {subValue && <span className="text-[10px] text-slate-400 font-medium mt-1 uppercase tracking-wider">{subValue}</span>}
        </div>
        <span className={`text-sm font-black tracking-tight ${highlight ? "text-indigo-700 font-black" : "text-slate-900"}`}>
            {value != null ? (typeof value === "number" ? value.toLocaleString() : value) : "N/A"}
            {value != null && suffix && <span className="text-[10px] ml-0.5 opacity-60">{suffix}</span>}
        </span>
    </div>
);

// ── Badge helper ────────────────────────────────────────────────────────────
const Badge = ({ children, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-100 text-blue-700",
        green: "bg-green-100 text-green-700",
        red: "bg-red-50 text-red-600 border border-red-100",
        purple: "bg-purple-50 text-purple-600 border border-purple-100",
        gray: "bg-slate-50 text-slate-500 border border-slate-100",
        emerald: "bg-emerald-50 text-emerald-600 border border-emerald-100",
        amber: "bg-amber-50 text-amber-700 border border-amber-100",
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${colors[color] || colors.blue}`}>
            {children}
        </span>
    );
};

// ── Helper: getSlopeCategory ────────────────────────────────────────────────
const getSlopeCategory = (s) => {
    if (s == null) return { text: "Unknown", color: "gray", icon: Info };
    if (s > 25) return { text: "Steep Terrain", color: "red", icon: AlertCircle };
    if (s > 15) return { text: "Moderate Slope", color: "amber", icon: Info };
    return { text: "Gentle / Flat", color: "green", icon: CheckCircle };
};



// ── TAB: Terrain (Dynamic LiDAR Slope Analysis) ───────────────────────
const TerrainTopographyViz = ({ flat, moderate, steep, onViewMap }) => {
    const total = (flat || 0) + (moderate || 0) + (steep || 0);
    if (total === 0) return (
        <div className="w-full h-[280px] bg-[#020617] rounded-[2.5rem] border border-white/5 flex flex-col items-center justify-center p-8 relative overflow-hidden group">
            {/* Background Tactical Matrix */}
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)',
                    backgroundSize: '30px 30px'
                }} />

            <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-600 animate-pulse" />
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Topography Offline</span>
                    </div>
                    <h4 className="text-sm font-black text-slate-300 uppercase tracking-tight">Awaiting Topographical Data</h4>
                    <p className="text-[10px] text-slate-500 font-bold max-w-[200px] leading-relaxed uppercase tracking-wider">
                        Initialize GIS Map Engine to generate high-fidelity terrain profile
                    </p>
                </div>

                <button
                    onClick={onViewMap}
                    className="group relative px-6 py-2.5 bg-indigo-500/10 hover:bg-indigo-500 text-indigo-400 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] border border-indigo-500/20 transition-all duration-300 shadow-lg shadow-indigo-500/5 active:scale-95"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        Open Map Hub
                        <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                </button>
            </div>

            {/* Scanner line decoration */}
            <motion.div
                animate={{ x: [-100, 400] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="absolute inset-y-0 w-px bg-indigo-500/20 blur-sm pointer-events-none"
            />
        </div>
    );

    const flatPct = (flat / total) * 100;
    const modPct = (moderate / total) * 100;
    const steepPct = (steep / total) * 100;

    // Generate a dynamic area path based on actual percentages
    // Left: Flat (Low), Middle: Moderate (Elevating), Right: Steep (High)
    const generatePath = () => {
        const h = 120; // max height
        const w = 400; // max width

        const x1 = (flatPct / 100) * w;
        const x2 = ((flatPct + modPct) / 100) * w;

        // Define points for a realistic terrain profile
        // Start (0, 110) -> end of flat segment -> end of moderate segment -> end of steep segment
        return `M 0 110 
                L ${x1 * 0.8} 110 
                C ${x1} 110, ${x1 + (x2 - x1) * 0.2} 100, ${x1 + (x2 - x1) * 0.5} 70 
                S ${x2} 30, ${x2 + (w - x2) * 0.2} 20 
                L ${w} 10 
                L ${w} ${h} L 0 ${h} Z`;
    };

    return (
        <div className="relative w-full h-[280px] bg-[#020617] rounded-[2.5rem] border border-white/5 overflow-hidden flex flex-col p-8 group shadow-2xl">
            {/* Background Tactical Matrix */}
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)',
                    backgroundSize: '30px 30px'
                }} />

            {/* Scanner Sync Overlay */}
            <div className="absolute inset-x-0 h-px bg-indigo-500/10 top-1/2 -translate-y-1/2" />
            <div className="absolute inset-y-0 w-px bg-indigo-500/10 left-1/2 -translate-x-1/2" />

            <div className="flex justify-between items-start relative z-10 mb-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] leading-none text-glow-indigo">LiDAR Profile Analysis</span>
                    </div>
                </div>
                <div className="text-right">
                    {/* <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-1">Topography Resolution</span> */}
                    <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 rounded text-[9px] font-black border border-indigo-500/20 uppercase tracking-tighter">High-Fidelity</span>
                    </div>
                </div>
            </div>

            {/* Dynamic Visualization */}
            <div className="flex-1 relative">
                <svg viewBox="0 0 400 120" className="w-full h-full overflow-visible drop-shadow-[0_0_15px_rgba(99,102,241,0.1)]">
                    <defs>
                        <linearGradient id="terrainGrad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
                            <stop offset={`${flatPct}%`} stopColor="#10b981" stopOpacity="0.1" />
                            <stop offset={`${flatPct}%`} stopColor="#f59e0b" stopOpacity="0.1" />
                            <stop offset={`${flatPct + modPct}%`} stopColor="#f59e0b" stopOpacity="0.1" />
                            <stop offset={`${flatPct + modPct}%`} stopColor="#f43f5e" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.2" />
                        </linearGradient>
                        <linearGradient id="strokeGrad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset={`${flatPct}%`} stopColor="#10b981" />
                            <stop offset={`${flatPct + 5}%`} stopColor="#f59e0b" />
                            <stop offset={`${flatPct + modPct}%`} stopColor="#f59e0b" />
                            <stop offset={`${flatPct + modPct + 5}%`} stopColor="#f43f5e" />
                            <stop offset="100%" stopColor="#f43f5e" />
                        </linearGradient>
                    </defs>

                    {/* Ground Plane Line */}
                    <line x1="0" y1="110" x2="400" y2="110" stroke="#ffffff10" strokeWidth="1" strokeDasharray="4 4" />

                    {/* Area fill */}
                    <motion.path
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        d={generatePath()}
                        fill="url(#terrainGrad)"
                    />

                    {/* Main Terrain Line */}
                    <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 2, ease: "easeInOut" }}
                        d={generatePath().split('L 400 120')[0]} // only the top line
                        fill="none"
                        stroke="url(#strokeGrad)"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />

                    {/* Scanner Needle */}
                    <motion.line
                        animate={{ x: [0, 400] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        x1="0" y1="0" x2="0" y2="120"
                        stroke="#6366f1"
                        strokeWidth="1.5"
                        className="opacity-50"
                        style={{ filter: 'drop-shadow(0 0 5px #6366f1)' }}
                    />
                </svg>

                {/* Data Points Overlays */}
                <div className="absolute inset-0 flex justify-between items-end pointer-events-none px-2 pb-1">
                    <div className="flex flex-col items-center group/p">
                        <div className="text-[12px] font-black text-emerald-400 tabular-nums">{flatPct.toFixed(0)}%</div>
                        <div className="h-6 w-px bg-emerald-500/30 mb-2 mt-1" />
                        <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Flat</span>
                    </div>
                    <div className="flex flex-col items-center pl-12">
                        <div className="text-[12px] font-black text-amber-400 tabular-nums">{modPct.toFixed(0)}%</div>
                        <div className="h-14 w-px bg-amber-500/30 mb-2 mt-1" />
                        <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Moderate</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="text-[12px] font-black text-rose-400 tabular-nums">{steepPct.toFixed(0)}%</div>
                        <div className="h-24 w-px bg-rose-500/30 mb-2 mt-1" />
                        <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Steep</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TerrainTab = ({ mapAnalysisStats, data, jurName, handleViewMap, hasPro, existingMfa, existingMda, plannedMfa, plannedMda, displayMfaLabel, displayMdaLabel }) => {
    // Determine if we have enough data to show the full layout
    const hasAnyData = mapAnalysisStats || (existingMfa > 0) || (existingMda > 0) || (plannedMfa > 0) || (plannedMda > 0);

    if (!hasAnyData) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-24"
            >
                <div className="relative mb-10">
                    <div className="absolute inset-0 bg-emerald-400/20 rounded-[2rem] blur-2xl scale-150" />
                    <div className="relative w-28 h-28 bg-gradient-to-br from-white to-emerald-50 rounded-[2rem] flex items-center justify-center border border-emerald-100/80 shadow-2xl shadow-emerald-500/10">
                        <Mountain className="w-12 h-12 text-emerald-500" strokeWidth={1.5} />
                    </div>
                </div>
                <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">Terrain Intelligence</h3>
                <p className="text-[15px] text-slate-400 font-medium max-w-sm text-center leading-relaxed">
                    Launch the Map Engine to analyze slope distribution and plan your {displayMfaLabel || "Max Floor"}/{displayMdaLabel || "Max Coverage"} footprint.
                </p>
                <button onClick={handleViewMap} className="mt-8 px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold text-[13px] hover:bg-slate-800 transition-all active:scale-[0.97]">
                    Run Terrain Analysis
                </button>
            </motion.div>
        );
    }

    const s = mapAnalysisStats || {};
    const totalTerrain = (s.flatArea || 0) + (s.moderateArea || 0) + (s.steepArea || 0);

    // Infrastructure Lifecycle (Zoning Max vs Detected vs Planned)
    const maxMfa = data.mfa_ft2 || 0;
    const existMfa = existingMfa ?? (s.mfaBuildingArea || 0);
    const newMfa = plannedMfa ?? (s.newMfa || 0);
    const totalMfa = existMfa + newMfa;

    const maxMda = data.mda_ft2 || 0;
    const existMda = existingMda ?? (s.mdaBuildingArea || 0);
    const newMda = plannedMda ?? (s.newMda || 0);
    const totalMda = existMda + newMda;

    return (
        <div className="space-y-10">
            {/* 1. Terrain Engine Hero - Command Center Intelligence Aesthetic */}
            <div className="bg-slate-900 p-4 text-white relative overflow-hidden shadow-2xl shadow-slate-900/60 group border border-white/5">

                <div className="relative z-10 flex flex-col xl:flex-row gap-8">

                    {/* Left Panel: The Engineering Visualization */}
                    <div className="flex-1 bg-slate-950/40 p-5 rounded-[2.5rem] border border-white/10 shadow-2xl">
                        <div className="flex flex-col lg:flex-row items-center gap-12">

                            {/* Visualization */}
                            <div className="flex-1 w-full max-w-[420px]">
                                <TerrainTopographyViz
                                    flat={s.flat || 0}
                                    moderate={s.moderate || 0}
                                    steep={s.steep || 0}
                                    onViewMap={handleViewMap}
                                />
                            </div>

                            {/* Standardized Intel Panel */}
                            <div className="flex-1 space-y-5">

                                {/* Identity & Status */}
                                <div className="space-y-2">
                                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-500/5 rounded-full border border-emerald-500/20">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                                        <span className="text-[12px] font-bold text-emerald-400 uppercase tracking-[0.3em]">GIS Engine Active</span>
                                    </div>

                                    <div className="space-y-4">
                                        <h2 className="text-5xl font-black text-white tracking-tighter leading-none">
                                            Terrain <span className="text-emerald-500">Analysis</span>
                                        </h2>
                                        <p className="text-slate-500 text-xs font-medium uppercase tracking-[0.2em] opacity-80">Topographical Analysis & Boundary Extraction</p>
                                    </div>
                                </div>

                                {/* Primary Metrics - Focused on Buildability */}
                                <div className="pt-4 border-t border-white/5">
                                    <div className="space-y-2">
                                        <div className="flex flex-col">
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-3xl font-black text-emerald-400 tabular-nums tracking-tighter">
                                                    {Math.round((data.mfa_ft2 || 0) + (jurName === 'Los Gatos' ? 0 : (data.mda_ft2 || 0))).toLocaleString()}
                                                </span>
                                                <span className="text-xs font-bold text-emerald-900 uppercase tracking-widest">ft²</span>
                                            </div>
                                            <div className="text-[9px] font-black text-slate-500 mt-1 uppercase tracking-[0.2em] opacity-60">
                                                {jurName !== 'Los Gatos' && data.mda_ft2 && data.mda_ft2 > 0 ? (
                                                    `(${displayMfaLabel || "Max Floor"}: ${Math.round(data.mfa_ft2 || 0).toLocaleString()} + ${displayMdaLabel || "Max Coverage"}: ${Math.round(data.mda_ft2 || 0).toLocaleString()})`
                                                ) : (
                                                    `(${displayMfaLabel || "Maximum Allowed Gross Floor Area"})`
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Right Panel: Constraint Analysis Hub */}
                    <div className="w-full xl:w-[400px] p-5 flex flex-col justify-between gap-10 relative overflow-hidden bg-slate-950/20 rounded-[2.5rem] border border-white/10 shadow-2xl">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b border-white/5 pb-5">
                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-[4px]">Critical Constraints</div>
                                <Shield className="w-4 h-4 text-slate-600" />
                            </div>

                            <div className="space-y-4">
                                {/* Regulatory Triggers - Horizontal Grid */}
                                <div className="grid grid-cols-3 gap-2.5">
                                    {[
                                        {
                                            label: 'Permit',
                                            type: 'CDP',
                                            status: data.requires_cdp ? 'CDP' : 'Clear',
                                            color: data.requires_cdp ? 'rose' : 'emerald',
                                        },
                                        {
                                            label: 'Compliance',
                                            type: 'Review',
                                            status: data.requires_design_review ? 'Board' : 'Exempt',
                                            color: data.requires_design_review ? 'amber' : 'emerald',
                                        },
                                        {
                                            label: 'Architectural',
                                            type: 'Limit',
                                            status: `${data.max_height_ft || '—'} FT`,
                                            color: 'sky',
                                        }
                                    ].map((item, i) => (
                                        <div key={i} className="flex flex-col p-3.5 bg-white/[0.03] backdrop-blur-md rounded-2xl border border-white/5 transition-all hover:bg-white/[0.08] hover:border-white/10 group/con">
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <div className={`w-1 h-1 rounded-full ${item.color === 'rose' ? 'bg-rose-500' :
                                                    item.color === 'amber' ? 'bg-amber-500' :
                                                        item.color === 'sky' ? 'bg-sky-500' :
                                                            'bg-emerald-500'
                                                    }`} />
                                                <span className="text-[7px] font-black text-slate-500 uppercase tracking-[0.2em]">{item.type}</span>
                                            </div>
                                            <div className={`text-[12px] font-black tracking-tight leading-none ${item.color === 'rose' ? 'text-rose-400' :
                                                item.color === 'amber' ? 'text-amber-400' :
                                                    item.color === 'sky' ? 'text-sky-400' :
                                                        'text-emerald-400'
                                                }`}>
                                                {item.status}
                                            </div>
                                            <span className="text-[6px] font-bold text-slate-600 uppercase tracking-widest mt-2 group-hover/con:text-slate-400 transition-colors">
                                                {item.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Summary Efficiency Metric - Compact Horizontal */}
                                <div className="p-3.5 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 flex items-center gap-4">
                                    <div className="relative w-10 h-10 flex-shrink-0">
                                        <svg className="w-full h-full transform -rotate-90">
                                            <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="3" fill="transparent" className="text-slate-800" />
                                            <motion.circle
                                                initial={{ pathLength: 0 }}
                                                animate={{ pathLength: totalTerrain > 0 ? (((s.flatArea || 0) + (s.moderateArea || 0)) / totalTerrain) : 0 }}
                                                transition={{ duration: 1.5 }}
                                                cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="3" fill="transparent"
                                                strokeDasharray="113"
                                                className="text-emerald-500"
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-[8px] font-black text-emerald-400">
                                                {totalTerrain > 0 ? Math.round(((s.flatArea || 0) + (s.moderateArea || 0)) / totalTerrain * 100) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-emerald-500/60 uppercase tracking-widest">Buildable Yield</span>
                                        <span className="text-[10px] font-bold text-slate-400">Surface Suitability Optimization</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleViewMap}
                                className="w-full relative group py-3 bg-white text-slate-950 rounded-xl font-black text-[13px] uppercase tracking-[4px] transition-all overflow-hidden shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <div className="absolute inset-0 bg-emerald-500 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                                <div className="relative z-10 flex items-center justify-center gap-3 group-hover:text-white transition-colors">
                                    <span>Access Map Hub</span>
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Integrated Building Intel (MFA & MDA Lifecycle) - Ultra Creative Radial Design */}
            <div className={`grid grid-cols-1 ${jurName !== 'Los Gatos' && maxMda > 0 ? 'md:grid-cols-2' : ''} gap-8`}>

                {/* MFA Radial Intel Card */}
                <div className="bg-white rounded-[4rem] p-12 border border-slate-100 shadow-2xl shadow-indigo-100/50 relative overflow-hidden group">
                    {/* Blueprint Pattern Background */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                        style={{ backgroundImage: 'linear-gradient(#475569 1px, transparent 1px), linear-gradient(90deg, #475569 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/50 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2 group-hover:scale-125 transition-transform duration-1000" />

                    <div className="flex flex-col xl:flex-row items-center gap-10">
                        {/* Gauge Section */}
                        <div className="relative">
                            <CircularGauge
                                existingPercentage={(existMfa / maxMfa) * 100 || 0}
                                plannedPercentage={(newMfa / maxMfa) * 100 || 0}
                                label={`${displayMfaLabel || "Max Floor"} USED`}
                                size={180}
                                existingColor="#6366f1"
                                plannedColor="#10b981"
                            />
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-indigo-600 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                                Capacity Status
                            </div>
                        </div>

                        {/* Data Section */}
                        <div className="flex-1 space-y-6 w-full">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm">
                                    <Home className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">{displayMfaLabel || "Maximum Floor Area [MFA]"}</h4>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">Max Limit: {maxMfa.toLocaleString()} sqft</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-4 rounded-3xl bg-slate-50/50 border border-slate-100 hover:bg-white hover:shadow-lg transition-all">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase">Existing</span>
                                    </div>
                                    <div className="text-lg font-black text-slate-800 tracking-tight">{Math.round(existMfa).toLocaleString()}</div>
                                </div>
                                <div className="p-4 rounded-3xl bg-emerald-50/30 border border-emerald-100/30 hover:bg-white hover:shadow-lg transition-all">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        <span className="text-[9px] font-black text-emerald-500 uppercase">Planned</span>
                                    </div>
                                    <div className="text-lg font-black text-emerald-600 tracking-tight">{Math.round(newMfa).toLocaleString()}</div>
                                </div>
                            </div>

                            <div className={`p-4 rounded-2xl flex items-center justify-between ${totalMfa > maxMfa ? 'bg-rose-50' : 'bg-slate-900'} transition-colors`}>
                                <div className="flex items-center gap-2">
                                    <ArrowUpRight className={`w-4 h-4 ${totalMfa > maxMfa ? 'text-rose-500' : 'text-emerald-400'}`} />
                                    <span className={`text-[10px] font-black uppercase tracking-widest ${totalMfa > maxMfa ? 'text-rose-600' : 'text-slate-400'}`}>
                                        {totalMfa > maxMfa ? 'Exceeding Limit' : 'Available Scale'}
                                    </span>
                                </div>
                                <div className={`text-sm font-black ${totalMfa > maxMfa ? 'text-rose-700' : 'text-white'}`}>
                                    {totalMfa > maxMfa ? (totalMfa - maxMfa).toLocaleString() : (maxMfa - totalMfa).toLocaleString()} <span className="text-[10px] opacity-40">sqft</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MDA Radial Intel Card */}
                {jurName !== 'Los Gatos' && maxMda > 0 && (
                    <div className="bg-white rounded-[4rem] p-12 border border-slate-100 shadow-2xl shadow-purple-100/50 relative overflow-hidden group">
                        {/* Blueprint Pattern Background */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                            style={{ backgroundImage: 'linear-gradient(#475569 1px, transparent 1px), linear-gradient(90deg, #475569 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50/50 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2 group-hover:scale-125 transition-transform duration-1000" />

                        <div className="flex flex-col xl:flex-row items-center gap-10">
                            {/* Gauge Section */}
                            <div className="relative">
                                <CircularGauge
                                    existingPercentage={(existMda / maxMda) * 100 || 0}
                                    plannedPercentage={(newMda / maxMda) * 100 || 0}
                                    label={`${displayMdaLabel || "Max Coverage"} USED`}
                                    size={180}
                                    existingColor="#a855f7"
                                    plannedColor="#10b981"
                                    trackColor="#fdf4ff"
                                />
                                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-purple-600 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                                    Capacity Status
                                </div>
                            </div>

                            {/* Data Section */}
                            <div className="flex-1 space-y-6 w-full">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm">
                                        <Maximize2 className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">{displayMdaLabel || "Maximum Development Area"}</h4>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">Max Limit: {maxMda.toLocaleString()} sqft</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-4 rounded-3xl bg-slate-50/50 border border-slate-100 hover:bg-white hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                            <span className="text-[9px] font-black text-slate-400 uppercase">Existing</span>
                                        </div>
                                        <div className="text-lg font-black text-slate-800 tracking-tight">{Math.round(existMda).toLocaleString()}</div>
                                    </div>
                                    <div className="p-4 rounded-3xl bg-emerald-50/30 border border-emerald-100/30 hover:bg-white hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                            <span className="text-[9px] font-black text-emerald-500 uppercase">Planned</span>
                                        </div>
                                        <div className="text-lg font-black text-emerald-600 tracking-tight">{Math.round(newMda).toLocaleString()}</div>
                                    </div>
                                </div>

                                <div className={`p-4 rounded-2xl flex items-center justify-between ${totalMda > maxMda ? 'bg-rose-50' : 'bg-slate-900'} transition-colors`}>
                                    <div className="flex items-center gap-2">
                                        <ArrowUpRight className={`w-4 h-4 ${totalMda > maxMda ? 'text-rose-500' : 'text-emerald-400'}`} />
                                        <span className={`text-[10px] font-black uppercase tracking-widest ${totalMda > maxMda ? 'text-rose-600' : 'text-slate-400'}`}>
                                            {totalMda > maxMda ? 'Exceeding Limit' : 'Available Scale'}
                                        </span>
                                    </div>
                                    <div className={`text-sm font-black ${totalMda > maxMda ? 'text-rose-700' : 'text-white'}`}>
                                        {totalMda > maxMda ? (totalMda - maxMda).toLocaleString() : (maxMda - totalMda).toLocaleString()} <span className="text-[10px] opacity-40">sqft</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 4. Infrastructure Metadata Footer */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-white/40 backdrop-blur-sm rounded-2xl px-8 py-5 border border-slate-100/50"
            >
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            GIS Terrain Engine • {(s.total || 0).toLocaleString()} cells analyzed
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        {[
                            { label: "Grid", value: s.contourInterval ? `${s.contourInterval}ft` : "Auto" },
                            { label: "Building", value: `${(s.building || 0).toLocaleString()} cells` },
                            { label: "Coverage", value: totalTerrain > 0 ? `${((s.buildingArea || 0) / totalTerrain * 100).toFixed(1)}%` : "N/A" },
                        ].map((m, i) => (
                            <div key={i} className="flex items-center gap-2 bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{m.label}</span>
                                <span className="text-[10px] font-black text-slate-700">{m.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

const ContourTab = ({ demContourTrace, demContourError, data, jurName, handleViewMap }) => {
    const details = demContourTrace?.contour_details || [];
    const hasData = demContourTrace && details.length > 0;

    if (!hasData) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center py-24"
            >
                <div className="relative mb-10">
                    <div className="absolute inset-0 bg-indigo-400/20 rounded-[2rem] blur-2xl scale-150" />
                    <div className="relative w-28 h-28 bg-gradient-to-br from-white to-indigo-50 rounded-[2rem] flex items-center justify-center border border-indigo-100/80 shadow-2xl shadow-indigo-500/10">
                        <Ruler className="w-12 h-12 text-indigo-500" strokeWidth={1.5} />
                    </div>
                </div>
                <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">Contour Intelligence</h3>
                <p className="text-[15px] text-slate-400 font-medium max-w-sm text-center leading-relaxed">
                    {demContourError || "Launch the Map Engine to generate high-fidelity topographic contour lines."}
                </p>
                <button onClick={handleViewMap} className="mt-8 px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold text-[13px] hover:bg-slate-800 transition-all active:scale-[0.97]">
                    Run Contour Analysis
                </button>
            </motion.div>
        );
    }


    // Split details into 4 columns for the engineering layout
    const chunkSize = Math.ceil(details.length / 4);
    const columns = [
        details.slice(0, chunkSize),
        details.slice(chunkSize, chunkSize * 2),
        details.slice(chunkSize * 2, chunkSize * 3),
        details.slice(chunkSize * 3)
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
        >
            <div className="bg-slate-900 rounded-[2rem] p-8 text-white relative overflow-hidden border border-white/5 shadow-2xl">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)', backgroundSize: '30px 30px' }} />

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-10">
                    <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                            <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Engineering Record</span>
                        </div>
                        <h2 className="text-3xl font-black tracking-tighter">Topographic <span className="text-indigo-400">Inventory</span></h2>
                        <p className="text-slate-400 text-xs font-medium max-w-md leading-relaxed">Official contour measurement breakdown (Variable L) for average slope compliance.</p>
                    </div>

                    <div className="flex gap-8">
                        <div className="text-center">
                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Interval</div>
                            <div className="text-2xl font-black text-white tabular-nums tracking-tighter">{demContourTrace?.interval_ft || 2} <span className="text-xs opacity-40 ml-0.5">FT</span></div>
                        </div>
                        <div className="text-center">
                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Lines</div>
                            <div className="text-2xl font-black text-white tabular-nums tracking-tighter">{demContourTrace?.contour_count || 0}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden group">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
                    {columns.map((col, colIdx) => (
                        <div key={colIdx} className="overflow-hidden">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50/80 border-b border-slate-100 text-[8px] font-black text-slate-400 uppercase tracking-widest">
                                    <tr>
                                        <th className="px-4 py-3">Contour</th>
                                        <th className="px-4 py-3 text-right">Length</th>
                                        <th className="px-4 py-3 text-right">%</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {col.map((detail, i) => (
                                        <tr key={i} className="hover:bg-slate-50/50 transition-colors group/row">
                                            <td className="px-4 py-2 bg-slate-50/30 text-[10px] font-black text-slate-900 border-r border-slate-50">{detail.elevation}</td>
                                            <td className="px-4 py-2 text-[10px] font-bold text-slate-600 text-right tabular-nums">{detail.length.toLocaleString()}</td>
                                            <td className="px-4 py-2 text-[10px] font-black text-indigo-500 text-right tabular-nums">
                                                {((detail.length / (demContourTrace?.contour_total_length_ft || 1)) * 100).toFixed(1)}%
                                            </td>
                                        </tr>
                                    ))}
                                    {/* Padding rows to maintain alignment */}
                                    {col.length < chunkSize && Array.from({ length: chunkSize - col.length }).map((_, i) => (
                                        <tr key={`pad-${i}`}>
                                            <td className="px-4 py-2 bg-slate-50/30 opacity-0">-</td>
                                            <td className="px-4 py-2 opacity-0">-</td>
                                            <td className="px-4 py-2 opacity-0">-</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                <div className="bg-slate-900 px-10 py-5 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,1)]" />
                        <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Calculated Variable (L)</span>
                    </div>
                    <div className="text-2xl font-black text-white tabular-nums tracking-tighter">
                        {Math.round(demContourTrace?.contour_total_length_ft || 0).toLocaleString()} <span className="text-xs opacity-50 ml-1">FT</span>
                    </div>
                </div>
            </div>

            <div className="flex items-start gap-4 bg-indigo-50 p-6 rounded-[1.5rem] border border-indigo-100">
                <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-indigo-100">
                    <Info className="w-4 h-4 text-indigo-500" />
                </div>
                <div>
                    <p className="text-[11px] font-black text-indigo-900 uppercase tracking-widest mb-1">Survey Intelligence Note</p>
                    <p className="text-xs text-indigo-700/80 font-medium leading-relaxed">
                        Contour segments are clipped exactly to the parcel boundary using USGS 1/3 Arc-Second LiDAR data. This inventory serves as the primary data source for {jurName} Average Slope worksheets.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};


// ── TAB: Overview ────────────────────────────────────────────────────────
const OverviewTab = ({
    data,
    slopeValue,
    jurName,
    slopeCat,
    SlopeIcon,
    calcResult,
    convMetric,
    parcelFt2,
    acres,
    buildablePct,
    existingMfaArea,
    isEditingArea,
    setIsEditingArea,
    tempArea,
    setTempArea,
    handleSaveArea,
    heightFt,
    setbacks,
    displayMfaLabel,
    displayMdaLabel,
    isDemPending
}) => {


    const rawFar = (data.mfa_ft2 && parcelFt2) ? (data.mfa_ft2 / parcelFt2) * 100 : 0;
    const farPct = Math.abs(rawFar - Math.round(rawFar)) < 0.15 ? Math.round(rawFar) : +rawFar.toFixed(1);

    return (
        <div className="space-y-6">
            {/* Hero KPI grid */}
            <div className={`grid grid-cols-2 ${jurName === 'Woodside'
                    ? 'lg:grid-cols-4'
                    : jurName === 'Los Gatos'
                        ? (calcResult?.netSiteArea !== undefined && calcResult?.netSiteArea !== null ? 'lg:grid-cols-4' : 'lg:grid-cols-3')
                        : ((data.mda_ft2 && data.mda_ft2 > 0) || (calcResult?.netSiteArea !== undefined && calcResult?.netSiteArea !== null) ? 'lg:grid-cols-4' : 'lg:grid-cols-3')
                } gap-4`}>
                {/* Slope */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10 transition-all"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-purple-100/50 rounded-xl p-2.5"><Mountain className="w-5 h-5 text-purple-600" /></div>
                        <Badge color="purple">{jurName}</Badge>
                    </div>
                    <div className="text-3xl font-black text-slate-900 tracking-tighter">{slopeValue}<span className="text-sm text-purple-600 ml-0.5">%</span></div>
                    <div className="text-[10px] font-black text-purple-600/60 uppercase tracking-widest mt-1">Avg Slope</div>
                    <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100 text-[10px] font-bold text-slate-500">
                        <SlopeIcon className="w-3.5 h-3.5" />{slopeCat.text}
                    </div>
                    <div className="mt-2 text-[9px] text-slate-400 font-medium leading-tight">
                        * Calculated slope may vary ±2–5% from field survey.
                    </div>
                </motion.div>

                {/* LUF / Zoning Metric */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-blue-100/50 rounded-xl p-2.5"><Hash className="w-5 h-5 text-blue-600" /></div>
                        <span title={calcResult?.formulaTooltip}
                            className="text-[10px] font-black px-3 py-1 rounded-full cursor-help uppercase tracking-widest border border-blue-100 bg-blue-50/50 text-blue-700">
                            {calcResult?.formulaLabel || "LUF"}
                        </span>
                    </div>
                    <div className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums flex items-baseline gap-1.5 flex-wrap">
                        {jurName === 'Los Gatos' && calcResult?.farValue
                            ? `${(calcResult.farValue * 100).toFixed(1)}%`
                            : jurName === 'Saratoga'
                                ? (convMetric != null ? convMetric.toFixed(3) : "N/A")
                                : (convMetric != null ? convMetric.toFixed(2) : "N/A")}
                        {convMetric != null && (
                            <span className="text-base font-bold text-slate-400">
                                {jurName === 'Los Gatos'
                                    ? `(${convMetric.toFixed(2)} ac)`
                                    : jurName === 'Saratoga'
                                        ? (
                                            <>
                                                ac
                                                {calcResult?.netSiteArea && (
                                                    <span className="text-sm font-bold text-slate-400 ml-2">
                                                        ({Math.round(calcResult.netSiteArea).toLocaleString()} ft²)
                                                    </span>
                                                )}
                                            </>
                                        )
                                        : 'ac'
                                }
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] font-black text-blue-600/60 uppercase tracking-widest mt-1">{calcResult?.formulaLabel || "Zoning Metric"}</div>
                    <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100 leading-relaxed break-words">
                        {jurName === 'Los Gatos' ? (
                            <Tooltip content={calcResult?.formulaTooltip}>
                                <span className="cursor-help hover:text-blue-600 transition-colors flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                    Combined Residential & Garage FAR (Hover for details)
                                </span>
                            </Tooltip>
                        ) : (
                            <div title={calcResult?.formulaTooltip}>
                                {calcResult?.formulaTooltip}
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Net Site Area (NSA) */}
                {calcResult?.netSiteArea !== undefined && calcResult?.netSiteArea !== null && jurName !== 'Saratoga' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.18 }}
                        className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/10 transition-all"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-emerald-100/50 rounded-xl p-2.5"><Maximize2 className="w-5 h-5 text-emerald-600" /></div>
                            <span className="text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-100 bg-emerald-50/50 text-emerald-700">
                                NSA
                            </span>
                        </div>
                        <div className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums">
                            {calcResult.netSiteArea.toLocaleString()}
                            <span className="text-base font-bold text-slate-400 ml-1">ft²</span>
                        </div>
                        <div className="text-[10px] font-black text-emerald-600/60 uppercase tracking-widest mt-1">Net Site Area</div>
                        <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100 leading-tight">
                            {(calcResult.netSiteArea / 43560).toFixed(3)} acres after slope deduction
                        </div>
                    </motion.div>
                )}

                {/* MDA */}
                {jurName !== 'Los Gatos' && data.mda_ft2 !== null && data.mda_ft2 !== undefined && data.mda_ft2 > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-green-300 hover:shadow-xl hover:shadow-green-500/10 transition-all"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-green-100/50 rounded-xl p-2.5"><Maximize2 className="w-5 h-5 text-green-600" /></div>
                            <Badge color="green">{displayMdaLabel || "Max Coverage"}</Badge>
                        </div>
                        <div className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums">
                            {data.mda_ft2?.toLocaleString() || "N/A"}
                            <span className="text-sm text-green-600 ml-1 font-bold">ft²</span>
                        </div>
                        <div className="text-[10px] font-black text-green-600/60 uppercase tracking-widest mt-1">{displayMdaLabel || "Max Development"}</div>
                        <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100">
                            {data.mda_ft2 ? `${(data.mda_ft2 / 43560).toFixed(3)} acres` : "Legal limit"}
                        </div>
                    </motion.div>
                )}

                {/* MFA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-indigo-300 hover:shadow-xl hover:shadow-indigo-500/10 transition-all"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-indigo-100/50 rounded-xl p-2.5"><Home className="w-5 h-5 text-indigo-600" /></div>
                        <div className="flex items-center gap-1.5">
                            <Badge color="blue">{displayMfaLabel || "Max Floor Area"}</Badge>
                            <Tooltip content="Floor Area Ratio (FAR) is the ratio of a building's total floor area (zoning envelope) to the size of the piece of land upon which it is built. For example, a 90% FAR means your total building area can be up to 90% of the lot size.">
                                <Info className="w-3.5 h-3.5 text-slate-400 hover:text-indigo-600 cursor-pointer transition-colors" />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums">
                        {data.mfa_ft2?.toLocaleString() || "N/A"}
                        <span className="text-sm text-indigo-600 ml-1 font-bold">ft²</span>
                    </div>
                    <div className="text-[10px] font-black text-indigo-600/60 uppercase tracking-widest mt-1">{displayMfaLabel || "Max Floor Area"}</div>
                    <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
                        <span>{data.mfa_ft2 ? `${(data.mfa_ft2 / 43560).toFixed(3)} acres` : "Building limit"}</span>
                        {farPct > 0 && (
                            <span className="font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                                {farPct}% FAR
                            </span>
                        )}
                    </div>
                </motion.div>

                {/* TFA (Total Allowable Floor Area) for Woodside */}
                {false && data.tfa_ft2 !== null && data.tfa_ft2 !== undefined && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.26 }}
                        className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/60 hover:border-violet-300 hover:shadow-xl hover:shadow-violet-500/10 transition-all"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="bg-violet-100/50 rounded-xl p-2.5"><Layers className="w-5 h-5 text-violet-600" /></div>
                            <Badge color="purple">{data.tfa_label || "Total Allowable Floor Area"}</Badge>
                        </div>
                        <div className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums">
                            {data.tfa_ft2?.toLocaleString() || "N/A"}
                            <span className="text-sm text-violet-600 ml-1 font-bold">ft²</span>
                        </div>
                        <div className="text-[10px] font-black text-violet-600/60 uppercase tracking-widest mt-1">{data.tfa_label || "Total Allowable Floor Area"}</div>
                        <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100">
                            {data.tfa_ft2 ? `${(data.tfa_ft2 / 43560).toFixed(3)} acres` : "Total limit"}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Opportunity Highlight Card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28 }}
                className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-8 text-white shadow-xl shadow-emerald-500/20 relative overflow-hidden"
            >
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[80px] rounded-full translate-x-1/3 -translate-y-1/3" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-emerald-200" />
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-100">Development Opportunity</span>
                        </div>
                        <h3 className="text-3xl font-black tracking-tighter mb-2">
                            {(data.mfa_ft2 - existingMfaArea).toLocaleString()} <span className="text-xl opacity-60">sqft</span>
                        </h3>
                        <p className="text-sm text-emerald-50/80 font-medium max-w-sm leading-relaxed">
                            Total untapped floor area potential legally allowed under current <span className="font-bold">{jurName}</span> zoning regulations.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                        {/* <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-200/60 mb-1">
                            Existing Footprint
                        </div>
                        <div className="text-xl font-black">{(data.gross_building_area_ft2 || 0).toLocaleString()} <span className="text-xs opacity-60">sqft</span></div>
                    </div> */}
                        <div className="w-px h-10 bg-white/10 hidden md:block" />
                        <div className="flex-1">
                            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-200/60 flex items-center justify-between">
                                <span className="whitespace-nowrap">Existing {displayMfaLabel || "House Size"}</span>
                                <button
                                    onClick={() => {
                                        setIsEditingArea(true);
                                        setTempArea(data.living_area_ft2 || "");
                                    }}
                                    className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <Edit2 className="w-3 h-3 text-emerald-200" />
                                </button>
                            </div>
                            {isEditingArea ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={tempArea}
                                        onChange={(e) => setTempArea(e.target.value)}
                                        className="bg-emerald-800/50 border border-emerald-400/30 rounded px-2 py-1 text-sm font-bold w-24 outline-none focus:border-emerald-300"
                                        autoFocus
                                    />
                                    <button onClick={handleSaveArea} className="p-1 hover:bg-emerald-500 rounded transition-colors"><Save className="w-4 h-4" /></button>
                                </div>
                            ) : (
                                <div className="text-xl font-black whitespace-nowrap">
                                    {data.living_area_ft2
                                        ? Math.round(data.living_area_ft2).toLocaleString()
                                        : (existingMfaArea ? Math.round(existingMfaArea).toLocaleString() : "-")
                                    }
                                    <span className="text-xs opacity-60 ml-1">sqft</span>
                                </div>
                            )}
                        </div>
                        <div className="w-px h-10 bg-white/10 hidden md:block" />
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-200/60 mb-1">Legal Limit ({displayMfaLabel || "Max Floor"})</div>
                            <div className="text-xl font-black">{(data.mfa_ft2 || 0).toLocaleString()} <span className="text-xs opacity-60">sqft</span></div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Development Potential gauge + Quick Insights row */}


            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Development Potential — Donut Chart */}
                {(() => {
                    const zoningLimit = data.mfa_ft2 || 0;
                    const livingArea = data.living_area_ft2 || 0;
                    const buildable = Math.max(0, zoningLimit - livingArea);
                    const total = zoningLimit || 1;
                    const livingPct = (livingArea / total) * 100;
                    const buildablePct = (buildable / total) * 100;

                    // SVG donut math
                    const size = 160;
                    const strokeWidth = 18;
                    const radius = (size - strokeWidth) / 2;
                    const circumference = 2 * Math.PI * radius;
                    const livingArc = (livingPct / 100) * circumference;
                    const buildableArc = (buildablePct / 100) * circumference;
                    const gap = 4; // gap between segments

                    return (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                            className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-sm border border-white/60 flex flex-col gap-4 group hover:shadow-xl hover:shadow-indigo-500/10 transition-all"
                        >
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Development Potential</div>

                            {/* Donut + Center Value */}
                            <div className="flex items-center justify-center relative" style={{ height: size }}>
                                <svg width={size} height={size} className="transform -rotate-90">
                                    <defs>
                                        <linearGradient id="grad-living" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#818cf8" />
                                            <stop offset="100%" stopColor="#6366f1" />
                                        </linearGradient>
                                        <linearGradient id="grad-buildable" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#34d399" />
                                            <stop offset="100%" stopColor="#10b981" />
                                        </linearGradient>
                                        <filter id="donut-shadow">
                                            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.15" />
                                        </filter>
                                    </defs>

                                    {/* Background track */}
                                    <circle
                                        cx={size / 2} cy={size / 2} r={radius}
                                        fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth}
                                    />

                                    {/* Living Area arc */}
                                    <circle
                                        cx={size / 2} cy={size / 2} r={radius}
                                        fill="none"
                                        stroke="url(#grad-living)"
                                        strokeWidth={strokeWidth}
                                        strokeDasharray={`${Math.max(0, livingArc - gap)} ${circumference - Math.max(0, livingArc - gap)}`}
                                        strokeDashoffset={0}
                                        strokeLinecap="round"
                                        filter="url(#donut-shadow)"
                                        className="transition-all duration-1000 ease-out"
                                        style={{ opacity: livingPct > 0 ? 1 : 0 }}
                                    />

                                    {/* Buildable arc */}
                                    <circle
                                        cx={size / 2} cy={size / 2} r={radius}
                                        fill="none"
                                        stroke="url(#grad-buildable)"
                                        strokeWidth={strokeWidth}
                                        strokeDasharray={`${Math.max(0, buildableArc - gap)} ${circumference - Math.max(0, buildableArc - gap)}`}
                                        strokeDashoffset={-(livingArc + gap)}
                                        strokeLinecap="round"
                                        filter="url(#donut-shadow)"
                                        className="transition-all duration-1000 ease-out"
                                        style={{ opacity: buildablePct > 0 ? 1 : 0 }}
                                    />
                                </svg>

                                {/* Center text */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-2xl font-black text-slate-800 tracking-tighter leading-none">
                                        {Math.round(buildable).toLocaleString()}
                                    </span>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">sqft left</span>
                                </div>
                            </div>

                            {/* Legend rows with hover highlight */}
                            <div className="flex flex-col gap-1.5">
                                {[
                                    { label: "Living Area", value: livingArea, pct: livingPct, dotClass: "bg-indigo-500", textClass: "text-indigo-600" },
                                    { label: "Buildable", value: buildable, pct: buildablePct, dotClass: "bg-emerald-500", textClass: "text-emerald-600" },
                                    { label: `${displayMfaLabel || "Zoning Limit"}`, value: zoningLimit, pct: 100, dotClass: "bg-amber-400", textClass: "text-amber-600", isBorder: true },
                                ].map((item, i) => (
                                    <div
                                        key={i}
                                        className={`flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors cursor-default ${item.isBorder ? "border-t border-slate-200/60 mt-1 pt-2.5" : ""}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2.5 h-2.5 rounded-full ${item.dotClass}`} />
                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{item.label}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className={`text-xs font-black ${item.textClass}`}>
                                                {Math.round(item.value).toLocaleString()}
                                            </span>
                                            <span className="text-[9px] text-slate-400 font-semibold">{Math.round(item.pct)}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    );
                })()}


                {/* Terrain & Complexity */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.35 }}
                    className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-sm border border-white/60 hover:shadow-xl hover:shadow-slate-500/10 transition-all flex flex-col"
                >
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Terrain Analysis</div>
                    <div className="flex-1 space-y-1">
                        <StatRow label="Avg Slope" value={slopeValue} suffix="%" highlight />
                        <StatRow label="Terrain Type" value={slopeCat.text} />
                        <StatRow label="Contour Lines" value={data.contour_count} />
                        <StatRow label="Total Length" value={data.contour_total_length_ft?.toLocaleString()} suffix=" ft" />
                    </div>
                </motion.div>


                {/* Building Summary */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-sm border border-white/60 hover:shadow-xl hover:shadow-slate-500/10 transition-all flex flex-col"
                >
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Regional Limits</div>
                    <div className="flex-1 space-y-1">
                        {jurName !== 'Los Gatos' && data.mda_ft2 !== null && data.mda_ft2 !== undefined && data.mda_ft2 > 0 && (
                            <StatRow label={displayMdaLabel || "Maximum Structural Coverage"} value={data.mda_ft2} suffix="ft²" highlight
                                subValue={calcResult?.formulaLabel || "Zoning Engine"} />
                        )}
                        <StatRow label={displayMfaLabel || "Maximum Floor Area"} value={data.mfa_ft2} suffix="ft²" highlight />
                        {farPct > 0 && (
                            <StatRow label="Floor Area Ratio (FAR)" value={`${farPct}%`} subValue="Ratio of building size to land" />
                        )}
                        {false && data.tfa_ft2 !== null && data.tfa_ft2 !== undefined && (
                            <StatRow label={data.tfa_label || "Total Allowable Floor Area"} value={data.tfa_ft2} suffix="ft²" highlight />
                        )}
                        <StatRow label="Existing Footprint" value={data.gross_building_area_ft2} suffix="ft²" />
                        <StatRow label="Lot Size" value={parcelFt2} suffix="ft²" />
                        {jurName === 'Los Gatos' && calcResult?.netSiteArea && (
                            <StatRow label="Net Site Area" value={calcResult.netSiteArea} suffix="ft²" />
                        )}
                        <StatRow label="Parcel Area" value={acres.toFixed(3)} suffix="ac" />
                    </div>
                </motion.div>
            </div>

            {/* Side-by-Side Comparison Feature */}
            <motion.div
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-indigo-50/30 backdrop-blur-md rounded-[2.5rem] p-1 border border-indigo-100/50"
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 items-stretch">
                    {/* Label & Legend */}
                    <div className="lg:col-span-2 p-8 flex flex-col justify-center">
                        <div className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mb-2">Phase 3c: Project Scoping</div>
                        <h3 className="text-3xl font-black text-slate-900 tracking-tighter mb-4 leading-none">Side-by-Side <br />Comparison</h3>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed max-w-xs">
                            Test your development targets against the maximum legal capacity defined by <span className="text-indigo-600 font-bold">{jurName}</span>.
                        </p>

                        <div className="mt-8 space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-2.5 h-2.5 rounded-full bg-blue-800" />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Existing Status</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Proposed Intent</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-800" />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Legal Maximum ({displayMfaLabel || "Max Floor Area"})</span>
                            </div>
                        </div>
                    </div>

                    {/* Interactive Comparison Tool */}
                    <div className="lg:col-span-3 p-4">
                        <ProjectScopingTool
                            analysisId={data.id}
                            mfa={data.mfa_ft2 || 0}
                            mfaLabel={displayMfaLabel || data.mfa_label}
                            maxHeight={heightFt}
                            setbackFront={data.setback_front_ft ?? setbacks.front_ft}
                            setbackRear={data.setback_rear_ft ?? setbacks.rear_ft}
                            setbackSide={data.setback_side_ft ?? setbacks.side_ft}
                            livingArea={data.living_area_ft2 || 0}
                            grossBuildingArea={data.gross_building_area_ft2 || 0}
                            currentProposedGfa={data.proposed_gfa_ft2 || 0}
                            currentProposedHeight={data.proposed_height_ft || 0}
                            currentProposedSetbackFront={data.proposed_setback_front_ft || 0}
                            currentProposedSetbackRear={data.proposed_setback_rear_ft || 0}
                            currentProposedSetbackSide={data.proposed_setback_side_ft || 0}
                            totalPlotted={data.total_plotted_area || 0}
                            compliance={data.compliance}
                        />
                    </div>
                </div>
            </motion.div>

            {/* Municipal Notes */}
            {(() => {
                const municipalNotes = Array.isArray(data.notes)
                    ? data.notes
                    : data.notes?.municipal_notes;
                if (!municipalNotes?.length) return null;
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="bg-amber-50/50 backdrop-blur-lg rounded-[2rem] p-8 border border-amber-100 shadow-xl shadow-amber-500/5 relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                            <Info className="w-32 h-32 text-amber-900" />
                        </div>
                        <div className="flex items-center gap-3 mb-6 relative z-10">
                            <div className="bg-amber-100 rounded-2xl p-2.5"><Info className="w-6 h-6 text-amber-600" /></div>
                            <h3 className="text-lg font-black text-slate-800 tracking-tight underline decoration-amber-300 underline-offset-4">Municipal Notes</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                            {municipalNotes.map((note, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.6 + i * 0.1 }}
                                    className="flex items-start gap-3 p-4 bg-white/60 rounded-2xl border border-amber-100 shadow-sm hover:shadow-md transition-all group"
                                >
                                    <div className="mt-1.5 w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 group-hover:scale-125 transition-transform" />
                                    <p className="text-sm text-slate-700 leading-relaxed font-medium">{note}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                );
            })()}
        </div>
    );
};

// ── TAB: Zoning ──────────────────────────────────────────────────────────
const ZoningTab = ({
    data,
    jurName,
    hasPro,
    heightFt,
    lotCovPct,
    setbacks,
    permitted,
    aduEligible,
    jaduEligible,
    duplexEleg,
    constraints,
    displayMfaLabel,
    displayMdaLabel,
    isDemPending
}) => (
    <div className="space-y-6">
        {/* Zone code header – Glassmorphic Dark */}
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-indigo-950 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl shadow-indigo-950/20"
        >
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full translate-x-1/3 -translate-y-1/3" />
            <div className="relative z-10 flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400 mb-2">Zoning District</div>
                    <div className="text-5xl font-black tracking-tighter">
                        {(() => {
                            const rawZone = data.zone_code || data.parcel_zone_code || "R-1";
                            const clean = rawZone.trim();
                            if (clean === "R1E") return "R1";
                            if (clean === "R-1E" || clean === "R-1:E" || clean === "R-1-E" || clean === "R1-E") return "R-1";
                            return clean;
                        })()}
                    </div>
                    <div className="text-sm text-indigo-200 mt-2 font-bold uppercase tracking-widest opacity-80">{jurName} Planning Code</div>
                </div>
                <div className="hidden sm:block">
                    <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-sm">
                        <Layers className="w-10 h-10 text-indigo-400" />
                    </div>
                </div>
            </div>
        </motion.div>

        {/* Building Capacity — Key development metrics */}
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="bg-white/80 backdrop-blur-xl rounded-xl p-4 sm:p-5 shadow-sm border border-slate-100"
        >
            <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-indigo-600" />
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Building Capacity</h3>
                </div>
                {jurName && (
                    <div className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded text-[9px] font-black text-indigo-600 uppercase tracking-widest">
                        {jurName}
                    </div>
                )}
            </div>

            <LockedOverlay hasPro={hasPro} label="Building Capacity">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-stretch">
                    {/* Spotlight Hero Section: Primary Allowance Capacity */}
                    <div className="lg:col-span-5 flex flex-col gap-2.5">
                        {/* Max Floor Area Spotlight Card */}
                        <div className="flex-1 bg-slate-900 text-white rounded-xl p-4 shadow-md relative overflow-hidden group hover:scale-[1.01] transition-all duration-300 border border-slate-800 flex flex-col justify-between">
                            {/* Glowing effect inside card */}
                            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-indigo-500/20 rounded-full blur-[25px] group-hover:scale-125 transition-all duration-500 pointer-events-none" />
                            <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-full blur-lg pointer-events-none" />

                            <div className="relative z-10 flex justify-between items-start">
                                <div className="space-y-0.5">
                                    <div className="px-2 py-0.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-full text-[8px] font-black uppercase tracking-wider inline-block">
                                        Primary Allowance
                                    </div>
                                    <h4 className="text-[11px] font-bold text-white/90 mt-0.5">{displayMfaLabel || "Max Floor Area"}</h4>
                                </div>
                                <span className="p-1.5 bg-white/5 border border-white/10 rounded-lg text-indigo-300 shadow-inner"><Home className="w-3.5 h-3.5" /></span>
                            </div>

                            <div className="relative z-10 mt-3">
                                <div className="text-2xl sm:text-3xl font-black text-white tracking-tighter leading-none tabular-nums flex items-baseline gap-0.5">
                                    {data.mfa_ft2 ? Math.round(data.mfa_ft2).toLocaleString() : "N/A"}
                                    <span className="text-[10px] text-indigo-400 font-extrabold">ft²</span>
                                </div>
                                <div className="text-[9px] text-indigo-300/60 mt-1 font-semibold flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-indigo-400" />
                                    {data.mfa_ft2 ? `${(data.mfa_ft2 / 43560).toFixed(3)} acres total` : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Max Development Area Spotlight Card (Only if present and non-zero) */}
                        {data.mda_ft2 != null && data.mda_ft2 > 0 && (
                            <div className="flex-1 bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 text-white rounded-xl p-4 shadow-md relative overflow-hidden group hover:scale-[1.01] transition-all duration-300 border border-slate-800/80 flex flex-col justify-between">
                                {/* Glowing effect inside card */}
                                <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-purple-500/20 rounded-full blur-[25px] group-hover:scale-125 transition-all duration-500 pointer-events-none" />
                                <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-full blur-lg pointer-events-none" />

                                <div className="relative z-10 flex justify-between items-start">
                                    <div className="space-y-0.5">
                                        <div className="px-2 py-0.5 bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded-full text-[8px] font-black uppercase tracking-wider inline-block">
                                            Footprint Limit
                                        </div>
                                        <h4 className="text-[11px] font-bold text-white/90 mt-0.5">{displayMdaLabel || "MDA"}</h4>
                                    </div>
                                    <span className="p-1.5 bg-white/5 border border-white/10 rounded-lg text-purple-300 shadow-inner"><Layers className="w-3.5 h-3.5" /></span>
                                </div>

                                <div className="relative z-10 mt-3">
                                    <div className="text-2xl sm:text-3xl font-black text-white tracking-tighter leading-none tabular-nums flex items-baseline gap-0.5">
                                        {Math.round(data.mda_ft2).toLocaleString()}
                                        <span className="text-[10px] text-purple-400 font-extrabold">ft²</span>
                                    </div>
                                    <div className="text-[9px] text-purple-300/60 mt-1 font-semibold flex items-center gap-1">
                                        <span className="w-1 h-1 rounded-full bg-purple-400" />
                                        {`${(data.mda_ft2 / 43560).toFixed(3)} acres total`}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Secondary Details Grid Section */}
                    <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {/* Lot Size */}
                        <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-emerald-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                <Maximize2 className="w-3.5 h-3.5 text-emerald-500 group-hover:scale-110 transition-transform" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Lot Size</span>
                            </div>
                            <div className="relative z-10 mt-auto">
                                <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums flex items-baseline">
                                    {data.parcel_area_ft2 ? Math.round(data.parcel_area_ft2).toLocaleString() : "N/A"}
                                    <span className="text-[10px] text-emerald-600 ml-0.5 font-bold">ft²</span>
                                </div>
                                <div className="text-[9px] text-slate-400 font-semibold mt-0.5 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-emerald-400" />
                                    {data.parcel_area_acres ? `${Number(data.parcel_area_acres).toFixed(3)} ac` : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Floor Area Ratio (FAR) */}
                        <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-blue-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                <Activity className="w-3.5 h-3.5 text-blue-500 group-hover:scale-110 transition-transform" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">FAR</span>
                            </div>
                            <div className="relative z-10 mt-auto">
                                <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums">
                                    {data.mfa_ft2 && data.parcel_area_ft2
                                        ? `${((data.mfa_ft2 / data.parcel_area_ft2) * 100).toFixed(1)}%`
                                        : "N/A"}
                                </div>
                                {/* Progress Bar */}
                                {data.mfa_ft2 && data.parcel_area_ft2 && (
                                    <div className="w-full bg-slate-200/60 h-1 rounded-full mt-1.5 overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${Math.min(100, (data.mfa_ft2 / data.parcel_area_ft2) * 100)}%` }}
                                        />
                                    </div>
                                )}
                                <div className="text-[8px] text-slate-400 font-bold mt-1 uppercase tracking-wider">MFA ÷ Lot</div>
                            </div>
                        </div>

                        {/* Lot Coverage */}
                        <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-indigo-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                <Shield className="w-3.5 h-3.5 text-indigo-500 group-hover:scale-110 transition-transform" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Coverage</span>
                            </div>
                            <div className="relative z-10 mt-auto">
                                <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums">
                                    {lotCovPct != null ? `${lotCovPct}%` : "N/A"}
                                </div>
                                {/* Progress Bar */}
                                {lotCovPct != null && (
                                    <div className="w-full bg-slate-200/60 h-1 rounded-full mt-1.5 overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${Math.min(100, lotCovPct)}%` }}
                                        />
                                    </div>
                                )}
                                <div className="text-[9px] text-slate-400 font-semibold mt-1 leading-none">
                                    {lotCovPct != null && data.parcel_area_ft2
                                        ? `${Math.round(data.parcel_area_ft2 * lotCovPct / 100).toLocaleString()} ft²`
                                        : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Avg Slope */}
                        <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-amber-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                <Mountain className="w-3.5 h-3.5 text-amber-500 group-hover:scale-110 transition-transform" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Avg Slope</span>
                                {isDemPending && (
                                    <span title="High-resolution terrain analysis in progress" className="ml-auto flex items-center gap-1 text-[8px] text-amber-500 font-bold animate-pulse">
                                        <svg className="w-2.5 h-2.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        Calculating…
                                    </span>
                                )}
                            </div>
                            <div className="relative z-10 mt-auto">
                                {isDemPending && data.avg_slope_percent_LAH == null ? (
                                    <div className="text-lg sm:text-xl font-black text-amber-400 tracking-tight animate-pulse">—</div>
                                ) : (
                                    <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums">
                                        {data.avg_slope_percent_LAH != null ? `${Number(data.avg_slope_percent_LAH).toFixed(1)}%` : "N/A"}
                                    </div>
                                )}
                                <div className="mt-1">
                                    {isDemPending ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wider bg-amber-50 text-amber-500 border border-amber-100">
                                            Terrain analysis in progress
                                        </span>
                                    ) : data.avg_slope_percent_LAH != null ? (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wider ${
                                            data.avg_slope_percent_LAH > 25
                                                ? "bg-red-50 text-red-600 border border-red-100"
                                                : data.avg_slope_percent_LAH > 15
                                                    ? "bg-amber-50 text-amber-600 border border-amber-100"
                                                    : "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                        }`}>
                                            {data.avg_slope_percent_LAH > 25 ? "Steep" : data.avg_slope_percent_LAH > 15 ? "Mod" : "Gentle"}
                                        </span>
                                    ) : (
                                        <span className="text-slate-400 font-semibold text-[10px]">—</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Max Height */}
                        <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-rose-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                <Ruler className="w-3.5 h-3.5 text-rose-500 group-hover:scale-110 transition-transform" />
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Max Height</span>
                            </div>
                            <div className="relative z-10 mt-auto">
                                <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums flex items-baseline">
                                    {heightFt != null ? heightFt : "N/A"}
                                    <span className="text-xs text-rose-600 ml-0.5 font-bold">ft</span>
                                </div>
                                <div className="text-[9px] text-slate-400 font-semibold mt-0.5">
                                    {heightFt != null ? `≈ ${Math.round(heightFt * 0.3048)} m` : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Lot Unit Factor (LUF) — only show if present */}
                        {data.lot_unit_factor != null && (
                            <div className="bg-slate-50/50 backdrop-blur-md rounded-xl p-3 border border-slate-100 hover:border-teal-200 hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative overflow-hidden">
                                <div className="flex items-center gap-1.5 text-slate-400 mb-1 relative z-10">
                                    <Activity className="w-3.5 h-3.5 text-teal-500 group-hover:scale-110 transition-transform" />
                                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Unit Factor</span>
                                </div>
                                <div className="relative z-10 mt-auto">
                                    <div className="text-lg sm:text-xl font-black text-slate-800 tracking-tight tabular-nums">
                                        {Number(data.lot_unit_factor).toFixed(3)}
                                    </div>
                                    <div className="text-[8px] text-slate-400 font-semibold mt-0.5 leading-tight">Slope-adj density</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </LockedOverlay>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Setbacks — locked for free */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 shadow-sm border border-white/60 hover:shadow-xl hover:shadow-indigo-500/5 transition-all"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-blue-100/50 rounded-2xl p-2.5"><Ruler className="w-5 h-5 text-blue-600" /></div>
                    <h3 className="text-base font-black text-slate-800 tracking-tight underline decoration-blue-100 underline-offset-4">Dimensional Standards</h3>
                </div>
                <LockedOverlay hasPro={hasPro} label="Setback Data">
                    <div className="space-y-1 pr-2">
                        <StatRow label="Front Setback" value={data.setback_front_ft ?? setbacks.front_ft} suffix=" ft" highlight />
                        <StatRow label="Rear Setback" value={data.setback_rear_ft ?? setbacks.rear_ft} suffix=" ft" />
                        <StatRow label="Side Setback" value={data.setback_side_ft ?? setbacks.side_ft} suffix=" ft" />
                        <StatRow label="Max Height" value={heightFt} suffix=" ft" highlight />
                        <StatRow label="Lot Coverage" value={lotCovPct != null ? `${lotCovPct}%` : "Verify"} />
                    </div>
                </LockedOverlay>
            </motion.div>

            {/* Permitted Uses — locked for free */}
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 shadow-sm border border-white/60 hover:shadow-xl hover:shadow-emerald-500/5 transition-all"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-emerald-100/50 rounded-2xl p-2.5"><Users className="w-5 h-5 text-emerald-600" /></div>
                    <h3 className="text-base font-black text-slate-800 tracking-tight underline decoration-emerald-100 underline-offset-4">Permitted Uses</h3>
                </div>
                <LockedOverlay hasPro={hasPro} label="Permitted Uses Data">
                    <div className="space-y-5">
                        <div className="flex flex-wrap gap-2">
                            {permitted.length > 0 ? permitted.map((use, i) => (
                                <Badge key={i} color="green">{use}</Badge>
                            )) : <span className="text-sm text-slate-400 font-bold italic">Not specifically listed</span>}
                        </div>
                        <div className="space-y-3 pt-5 border-t border-slate-100/50">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">ADU Eligible</span>
                                <Badge color={aduEligible ? "green" : "red"}>{aduEligible ? "✓ Eligible" : "✗ Restricted"}</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">JADU Eligible</span>
                                <Badge color={jaduEligible ? "green" : "red"}>{jaduEligible ? "✓ Eligible" : "✗ Restricted"}</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Duplex (SB9)</span>
                                <Badge color={duplexEleg ? "green" : "amber"}>{duplexEleg ? "✓ SB-9 Eligible" : "Check Local Rules"}</Badge>
                            </div>
                        </div>
                    </div>
                </LockedOverlay>
            </motion.div>
        </div>

        {/* Notes from zoning rules */}
        {constraints.notes && (
            <div className="bg-indigo-50/50 backdrop-blur-md border border-indigo-100/50 rounded-2xl p-5 text-sm text-indigo-900 leading-relaxed font-medium">
                <span className="font-bold uppercase text-[10px] tracking-widest text-indigo-500 block mb-1">Planning Agency Note</span>
                {constraints.notes}
            </div>
        )}
    </div>
);

const ComplianceTab = ({
    data,
    slopeValue,
    designRev,
    hasPro
}) => (
    <div className="space-y-6">
        {/* Slope compliance bar – Glassmorphic */}
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-sm border border-white/60"
        >
            <div className="flex items-center gap-3 mb-8">
                <div className="bg-indigo-100 rounded-2xl p-2.5"><Shield className="w-6 h-6 text-indigo-600" /></div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight underline decoration-indigo-200 underline-offset-4">Topographic Compliance</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { label: "Standard Build (< 15%)", pass: slopeValue < 15, threshold: "< 15%" },
                    { label: "Hillside Review (< 25%)", pass: slopeValue < 25, threshold: "< 25%" },
                    { label: "Critical Slope (< 30%)", pass: slopeValue < 30, threshold: "< 30%" },
                    { label: "Geotechnical Rpt (> 15%)", pass: slopeValue <= 15, threshold: "> 15%", invert: true },
                ].map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 + i * 0.1 }}
                        className="flex items-center justify-between p-4 rounded-2xl bg-slate-50/50 border border-slate-100/50 hover:bg-white hover:shadow-md hover:border-white transition-all group"
                    >
                        <div className="flex items-center gap-3">
                            {item.invert
                                ? (item.pass
                                    ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    : <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />)
                                : (item.pass
                                    ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    : <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />)}
                            <span className="text-[11px] font-black text-slate-500 uppercase tracking-tight group-hover:text-slate-900 transition-colors">{item.label}</span>
                        </div>
                        <Badge color={item.pass ? "green" : (item.invert ? "amber" : "red")}>
                            {item.pass ? "COMPLIANT" : (item.invert ? "REQUIRED" : "NON-COMPLIANT")}
                        </Badge>
                    </motion.div>
                ))}
            </div>
        </motion.div>

        {/* Required documentation – Glassmorphic */}
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-sm border border-white/60"
        >
            <div className="flex items-center gap-3 mb-8">
                <div className="bg-amber-100 rounded-2xl p-2.5"><Info className="w-6 h-6 text-amber-600" /></div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight underline decoration-amber-200 underline-offset-4">Prerequisite Documentation</h3>
            </div>
            <LockedOverlay hasPro={hasPro} label="Prerequisite Checklist">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { doc: "Topographic Survey", required: true },
                        { doc: "Grading & Drainage Plan", required: slopeValue > 10 },
                        { doc: "Geotechnical Report", required: slopeValue > 15 },
                        { doc: "Coastal Permit", required: data.requires_cdp },
                        { doc: "Design Review", required: designRev },
                        { doc: "CEQA Review", required: slopeValue > 25 },
                    ].map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + i * 0.05 }}
                            className="flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-100 hover:shadow-md transition-all group"
                        >
                            <span className="text-[11px] font-black text-slate-600 uppercase tracking-tight group-hover:text-slate-900 transition-colors leading-tight">{item.doc}</span>
                            <Badge color={item.required ? "amber" : "green"}>
                                {item.required ? "REQUIRED" : "PASSED"}
                            </Badge>
                        </motion.div>
                    ))}
                </div>
            </LockedOverlay>
        </motion.div>
    </div>
);

// ── TAB: Reports ─────────────────────────────────────────────────────────
const ReportsTab = ({
    data,
    jurName,
    timeline,
    hasPro,
    canExport,
    handleExport,
    formatDate,
    setIsShareModalOpen,
    handleViewMap
}) => (
    <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Development Timeline – Glassmorphic */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-2 bg-white/70 backdrop-blur-xl rounded-[2rem] p-8 shadow-sm border border-white/60 flex flex-col"
            >
                <div className="flex items-center gap-4 mb-8">
                    <div className="bg-indigo-100 rounded-2xl p-3"><Clock className="w-6 h-6 text-indigo-600" /></div>
                    <div>
                        <h3 className="text-xl font-black text-slate-800 tracking-tight">Projected Timeline</h3>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Municipal Estimation • {jurName}</p>
                    </div>
                </div>
                <LockedOverlay hasPro={hasPro} label="Development Timeline">
                    <div className="flex-1 flex flex-col justify-center min-h-[200px]">
                        {timeline ? (
                            <TimelineSparkline phases={timeline.phases} total={timeline.total_months} />
                        ) : (
                            <div className="text-center py-10">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                    <Activity className="w-8 h-8 text-slate-200" />
                                </div>
                                <p className="text-sm font-bold text-slate-400">Timeline modeling unavailable for this jurisdiction</p>
                            </div>
                        )}
                    </div>
                </LockedOverlay>
            </motion.div>

            {/* Analysis Metadata – Refined List */}
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-slate-50/50 backdrop-blur-xl rounded-[2rem] p-8 border border-slate-200/50 flex flex-col shadow-inner"
            >
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">System Metadata</h3>
                <div className="space-y-4">
                    {[
                        ["APN Source", data.apn],
                        ["Contour Accuracy", data.notes_contour_interval_used_ft ? `${data.notes_contour_interval_used_ft}ft Resolution` : "Standard"],
                        ["GIS Engine", "LIDAR-V2"],
                        ["Jur Code", data.city_code],
                        ["Internal ID", `#${data.id?.toString()?.slice(-6) || "N/A"}`],
                    ].map(([label, val], i) => val && (
                        <div key={label} className="flex justify-between items-center py-2 border-b border-slate-200/50 last:border-0">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-tight">{label}</span>
                            <span className="font-bold text-slate-800 font-mono text-xs truncate ml-4 bg-white/50 px-2 py-0.5 rounded-md border border-white">{val}</span>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    </div>
);

const ADUTab = ({ data, aduEligible, jaduEligible, jurName, livingArea }) => {
    const mainLivingArea = livingArea || data.living_area_ft2 || 0;
    const attachedMax = Math.min(1200, Math.max(800, Math.round(mainLivingArea * 0.5)));
    const detachedMax = 1200;
    const jaduMax = 500;

    const isCommercial = ["CN", "CD", "CRS", "CT", "OA", "CD/R3"].includes((data?.zone_code || "").toUpperCase().trim());

    if (isCommercial && !aduEligible && !jaduEligible) {
        return (
            <div className="space-y-8">
                {/* ── Intelligence Hero ── */}
                <div className="relative bg-slate-900 rounded-[3rem] p-10 overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="space-y-4 text-center md:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 rounded-full border border-white/10 text-amber-300 text-[10px] font-black uppercase tracking-widest leading-none">
                                <Info className="w-3.5 h-3.5 text-amber-300" />
                                Commercial Zone Constraint
                            </div>
                            <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter leading-none">
                                ADUs Not Eligible
                            </h2>
                            <p className="text-slate-400 text-sm font-medium max-w-lg leading-relaxed">
                                Accessory Dwelling Units (ADUs) are not permitted in strictly commercial zoning districts ({data?.zone_code}) under the local jurisdiction of {jurName}.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-amber-50/50 p-6 rounded-3xl border border-amber-100/50 flex items-start gap-4">
                    <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <h5 className="font-bold text-amber-900 text-sm">Mixed-Use Opportunities</h5>
                        <p className="text-xs text-amber-800 leading-relaxed">
                            While standalone residential ADUs are restricted, mixed-use commercial and residential developments (such as upper-story apartments) may be permitted depending on the specific zone regulations.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* ── Intelligence Hero ── */}
            <div className="relative bg-slate-900 rounded-[3rem] p-10 overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="space-y-4 text-center md:text-left">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 rounded-full border border-white/10 text-indigo-300 text-[10px] font-black uppercase tracking-widest leading-none">
                            <Sparkles className="w-3.5 h-3.5" />
                            California State Potential
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter leading-none">
                            Add up to <span className="text-indigo-400">2 Extra Units</span>
                        </h2>
                        <p className="text-slate-400 text-sm font-medium max-w-lg leading-relaxed">
                            Under California mandate (AB 68), you are typically allowed one ADU and one JADU regardless of local density limits.
                        </p>
                    </div>
                    <div className="px-8 py-6 bg-white/5 backdrop-blur-xl rounded-[2rem] border border-white/10 text-center min-w-[200px]">
                        <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Max Combined Area</div>
                        <div className="text-4xl font-black text-white tabular-nums tracking-tighter">{(detachedMax + jaduMax).toLocaleString()}<span className="text-xs text-indigo-500 ml-1">ft²</span></div>
                    </div>
                </div>
            </div>

            {/* ── Selection Bento Grid ── */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

                {/* 1. JADU: The Internal Conversion */}
                <motion.div
                    whileHover={{ y: -5 }}
                    className="md:col-span-4 bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-xl shadow-indigo-500/5 group flex flex-col"
                >
                    <Tooltip content="A Junior ADU (JADU) is a unit up to 500 sq. ft. contained entirely within an existing single-family residence. Requires an efficiency kitchen and separate entry.">
                        <div className="flex justify-between items-start mb-8">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100/50">
                                <Users className="w-6 h-6" />
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <Badge color="blue">FASTEST</Badge>
                                <Info className="w-3 h-3 text-slate-300" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <h4 className="text-xl font-black text-slate-900 tracking-tight leading-none mb-2">Junior ADU</h4>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-6">Internal Conversion</p>
                            <div className="text-3xl font-black text-slate-900 mb-6 tabular-nums">{jaduMax}<span className="text-xs text-slate-400 font-medium ml-1">sqft max</span></div>

                            <div className="space-y-3 pt-6 border-t border-slate-50">
                                {[
                                    "Conversion of existing rooms",
                                    "Must include efficiency kitchen",
                                    "Owner occupancy required (LAH)"
                                ].map((text, i) => (
                                    <div key={i} className="flex gap-2 items-center text-[11px] font-bold text-slate-500">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/20" />
                                        {text}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Tooltip>
                </motion.div>

                {/* 2. Attached/Detached Master Card */}
                <motion.div
                    whileHover={{ y: -5 }}
                    className="md:col-span-8 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-[2.5rem] p-10 text-white shadow-xl shadow-indigo-500/20 relative flex flex-col lg:flex-row gap-10"
                >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2" />
                    <div className="relative z-10 flex-1 space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-white border border-white/20">
                                <Maximize2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="text-2xl font-black tracking-tight leading-none">Full ADU</h4>
                                <p className="text-[10px] text-white/60 font-bold uppercase tracking-widest mt-1">Attached or Detached</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Tooltip content="An Attached ADU is physically joined to the primary house. Max size is 1,200 sq. ft. or 50% of living area (whichever is less), but state law ensures at least 800 sq. ft. is allowed.">
                                <div className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/[0.08] transition-all duration-300 cursor-help group/tip">
                                    <div className="flex justify-between items-center mb-1">
                                        <div className="text-[9px] font-black text-indigo-200 uppercase">Attached Limit</div>
                                        <Info className="w-2.5 h-2.5 text-white/20 group-hover/tip:text-indigo-400 transition-colors" />
                                    </div>
                                    <div className="text-2xl font-black mb-1">{attachedMax.toLocaleString()}<span className="text-[10px] ml-1 opacity-60">sf</span></div>
                                    <p className="text-[9px] text-white/40 font-bold uppercase leading-tight">Expands existing structure</p>
                                </div>
                            </Tooltip>
                            <Tooltip content="A Detached ADU is a standalone building separated from the main residence. State law allows up to 1,200 sq. ft. (minimum 800 sq. ft. floor) with 4-ft side and rear setbacks.">
                                <div className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/[0.08] transition-all duration-300 cursor-help group/tip">
                                    <div className="flex justify-between items-center mb-1">
                                        <div className="text-[9px] font-black text-indigo-200 uppercase">Detached Limit</div>
                                        <Info className="w-2.5 h-2.5 text-white/20 group-hover/tip:text-emerald-400 transition-colors" />
                                    </div>
                                    <div className="text-2xl font-black mb-1">{detachedMax.toLocaleString()}<span className="text-[10px] ml-1 opacity-60">sf</span></div>
                                    <p className="text-[9px] text-white/40 font-bold uppercase leading-tight">Separate standalone building</p>
                                </div>
                            </Tooltip>
                        </div>
                        <div className="pt-6 space-y-3">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5" />
                                <p className="text-xs font-medium text-white/80 leading-relaxed">
                                    State Law overrides local density. Full ADUs up to <span className="font-bold">800 sqft</span> are exempt from most restrictive local FAR limits.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 w-full lg:w-56 bg-black/20 rounded-[2rem] p-6 flex flex-col justify-between border border-white/5 backdrop-blur-sm shadow-inner mt-4 lg:mt-0">
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-4">Buildability Score</div>
                                <CircularGauge
                                    existingPercentage={aduEligible ? 85 : 0}
                                    size={100}
                                    plannedColor="#10b981"
                                    trackColor="#ffffff10"
                                    label="POTENTIAL"
                                    textColor="text-white"
                                />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <Badge color="green">HIGH POTENTIAL</Badge>
                                <span className="text-[9px] font-bold text-white/30 uppercase tracking-tighter mt-1">Optimal Zone Conditions</span>
                            </div>
                        </div>
                        <div className="text-[8px] font-black text-white/20 text-center mt-6 uppercase tracking-widest">Calculated for {jurName}</div>
                    </div>
                </motion.div>

                {/* 3. The Rules HUD */}
                <div className="md:col-span-12 bg-white/40 backdrop-blur-sm rounded-3xl p-10 border border-slate-200/50 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Ruler, label: "Setbacks", val: "4 FT Min", desc: "State law overrides local setbacks.", tip: "Government Code 65852.2 limits side and rear setbacks to a maximum of 4 feet for ADUs. Local rules requiring larger setbacks are superseded." },
                        { icon: ArrowUpRight, label: "Height", val: "16-18 FT", desc: "Max height typically allowed.", tip: "ADUs are generally allowed up to 16 ft (or 18 ft if near transit or on a lot with a multi-story primary residence)." },
                        { icon: Shield, label: "Parking", val: "No Req", desc: "Exempt if near public transit.", tip: "No parking is required if the ADU is within 0.5 miles of public transit, within an architecturally significant district, or part of an existing structure." }
                    ].map((item, i) => (
                        <Tooltip key={i} content={item.tip}>
                            <div className="flex gap-5 cursor-help group/rule">
                                <div className="p-3 bg-slate-900 rounded-2xl text-white h-fit group-hover/rule:bg-indigo-600 transition-colors">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <div className="relative">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.label}</div>
                                        <Info className="w-2 h-2 text-slate-300 opacity-50" />
                                    </div>
                                    <div className="text-lg font-black text-slate-900 leading-none mb-1">{item.val}</div>
                                    <p className="text-[11px] font-medium text-slate-500 leading-tight">{item.desc}</p>
                                </div>
                            </div>
                        </Tooltip>
                    ))}
                </div>

            </div>

            {/* ── Mandate Info ── */}
            <div className="flex items-center gap-4 bg-indigo-50/50 p-6 rounded-3xl border border-indigo-100/50">
                <Info className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                <p className="text-xs text-indigo-900 font-medium leading-relaxed">
                    Local ordinances may apply but cannot prohibit ADUs entirely. This analysis reflects standard <span className="font-bold">California Housing & Community Development (HCD)</span> protocols.
                </p>
            </div>
        </div>
    );
};



// ── Main component ──────────────────────────────────────────────────────────
const AnalysisResults = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentAnalysis } = useSelector((state) => state.analysis);
    const { canExport, hasPro } = useFeatureAccess();

    const [activeTab, setActiveTab] = useState("overview");
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [showMapWarningModal, setShowMapWarningModal] = useState(false);
    const [pendingExport, setPendingExport] = useState(false);

    // --- Manual Living Area Override State ---
    const [isEditingArea, setIsEditingArea] = useState(false);
    const [tempArea, setTempArea] = useState("");

    // Safe data extraction for hooks
    const currentData = currentAnalysis?.data;
    const [liveData, setLiveData] = useState(null); // refreshed by DEM polling
    const data = useMemo(() => liveData || currentData || {}, [liveData, currentData]);

    // ── Derived values (Memoized for performance) ───────────────────────────
    const slopeValue = useMemo(() => data.avg_slope_percent_LAH ?? data.slope_percent ?? 0, [data]);

    // ── DEM background-task polling ─────────────────────────────────────────
    // When the DEM analysis is still running (dem_status != 'Complete'),
    // poll every 30s and update the displayed slope automatically.
    const demStatus = data?.notes?.dem_status;
    const isDemPending = demStatus !== "Complete" && data?.id;
    useEffect(() => {
        if (!isDemPending) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const poll = setInterval(async () => {
            try {
                const res = await axios.get(`${API_BASE}/analyses/${data.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const fresh = res.data;
                if (fresh?.notes?.dem_status === "Complete" || fresh?.avg_slope_percent_LAH !== data.avg_slope_percent_LAH) {
                    setLiveData(fresh);
                    if (fresh?.notes?.dem_status === "Complete") clearInterval(poll);
                }
            } catch (e) {
                console.warn("DEM poll error:", e.message);
            }
        }, 30000); // every 30 seconds
        return () => clearInterval(poll);
    }, [isDemPending, data.id, data.avg_slope_percent_LAH]);
    const [customStructures, setCustomStructures] = useState([]);
    const [mapAnalysisStats, setMapAnalysisStats] = useState(null);
    const [demContourTrace, setDemContourTrace] = useState(data.contour_details ? {
        success: true,
        contour_count: data.contour_count,
        contour_total_length_ft: data.contour_total_length_ft,
        contour_details: data.contour_details,
        interval_ft: data.notes_contour_interval_used_ft || 2,
        cached: true
    } : null);
    const [demContourError, setDemContourError] = useState(null);

    // 🔄 Sync demContourTrace with data.contour_details when it changes (e.g. after search or DB load)
    useEffect(() => {
        // Only initialize with skeleton if we don't already have a full trace with geometry
        if (data?.contour_details && data.contour_details.length > 0 && !demContourTrace?.features) {
            setDemContourTrace({
                success: true,
                contour_count: data.contour_count,
                contour_total_length_ft: data.contour_total_length_ft,
                contour_details: data.contour_details,
                interval_ft: data.notes_contour_interval_used_ft || 2,
                cached: true
            });
        }
    }, [data.contour_details, data.contour_count, data.contour_total_length_ft, data.notes_contour_interval_used_ft, demContourTrace?.features]);

    useEffect(() => {
        const fetchCustomStructures = async () => {
            if (!data?.address) return;
            try {
                const response = await axios.get(`${API_BASE}/api/custom-structures`, {
                    params: { address: data.address, apn: data.apn }
                });
                setCustomStructures(response.data.structures || []);
            } catch (err) {
                console.error("Failed to fetch custom structures:", err);
            }
        };

        fetchCustomStructures();
    }, [data.address, data.apn, activeTab]);

    // Auto-load saved map analysis snapshot from DB
    useEffect(() => {
        if (!data?.id) return;
        axios.get(`${API_BASE}/api/map-analysis-snapshot/${data.id}`)
            .then(res => {
                const snap = res.data?.snapshot;
                if (snap) {
                    setMapAnalysisStats({
                        flat: snap.flat_cells,
                        moderate: snap.moderate_cells,
                        steep: snap.steep_cells,
                        building: snap.building_cells,
                        total: snap.total_cells,
                        flatArea: snap.flat_area_sqft,
                        moderateArea: snap.moderate_area_sqft,
                        steepArea: snap.steep_area_sqft,
                        buildingArea: snap.building_area_sqft,
                        accessoryArea: snap.accessory_area_sqft,
                        totalArea: snap.total_area_sqft,
                        mfaBuildingArea: snap.mfa_building_area_sqft,
                        mdaBuildingArea: snap.mda_building_area_sqft,
                        contourInterval: snap.contour_interval_ft,
                    });
                    console.log('📸 Loaded saved map analysis snapshot from DB');
                }
            })
            .catch(err => console.warn('Could not load map snapshot:', err.message));
    }, [data.id]);

    const acres = useMemo(() => data.parcel_area_acres || 0, [data]);
    const parcelFt2 = useMemo(() => data.parcel_area_ft2 || (acres * 43560), [data, acres]);

    const handleMapAnalysisComplete = useCallback((stats) => {
        setMapAnalysisStats(stats);
    }, []);

    const handleContoursGenerated = useCallback((trace) => {
        setDemContourTrace(trace);
    }, []);

    // Existing MFA: same formula as MapModal's mfaAlreadyBuilt
    // Priority: 1) Manual structures  2) Map terrain analysis  3) DB values
    const existingMfaArea = useMemo(() => {
        const manualMfa = customStructures
            .filter(s => s.type === 'EXISTING_MFA')
            .reduce((sum, s) => sum + (s.area || 0), 0);

        if (manualMfa > 0) return manualMfa;
        if (mapAnalysisStats?.mfaBuildingArea > 0) return mapAnalysisStats.mfaBuildingArea;
        return data.gross_building_area_ft2 || data.living_area_ft2 || 0;
    }, [customStructures, mapAnalysisStats, data.gross_building_area_ft2, data.living_area_ft2]);

    // Priority: 1) Manual EXISTING_MDA  2) Map terrain analysis
    const existingMdaArea = useMemo(() => {
        const manualMda = customStructures
            .filter(s => s.type === 'EXISTING_MDA')
            .reduce((sum, s) => sum + (s.area || 0), 0);

        if (manualMda > 0) return manualMda;
        return mapAnalysisStats?.mdaBuildingArea || 0;
    }, [customStructures, mapAnalysisStats]);

    // Planned MFA: NEW_MFA + EXTEND_MFA (net) + EXISTING_MFA delta
    const plannedMfaArea = useMemo(() => {
        return customStructures
            .filter(s => ['NEW_MFA', 'EXTEND_MFA', 'EXISTING_MFA'].includes(s.type))
            .reduce((sum, s) => {
                const area = s.area || 0;
                const original = s.original_area_sqft || area;
                if (s.type === 'EXISTING_MFA' || s.type === 'EXTEND_MFA') {
                    return sum + Math.max(0, area - original);
                }
                return sum + area;
            }, 0);
    }, [customStructures]);

    // Planned MDA: NEW_MDA + MDA (fallback) + CUSTOM_MDA + EXISTING_MDA delta
    const plannedMdaArea = useMemo(() => {
        return customStructures
            .filter(s => ['NEW_MDA', 'MDA', 'CUSTOM_MDA', 'EXISTING_MDA'].includes(s.type))
            .reduce((sum, s) => {
                const area = s.area || 0;
                const original = s.original_area_sqft || area;
                if (s.type === 'EXISTING_MDA') {
                    return sum + Math.max(0, area - original);
                }
                return sum + area;
            }, 0);
    }, [customStructures]);

    const buildablePct = useMemo(() => (data.mda_ft2 && parcelFt2
        ? +((data.mda_ft2 / parcelFt2) * 100).toFixed(1) : 0), [data.mda_ft2, parcelFt2]);

    const slopeCat = useMemo(() => getSlopeCategory(slopeValue), [slopeValue]);
    const SlopeIcon = slopeCat.icon;

    // Jurisdiction
    const jur = useMemo(() => detectJurisdiction(data.address), [data.address]);
    const jurisdictionInfo = useMemo(() => getJurisdictionInfo(jur), [jur]);
    const jurName = useMemo(() => jurisdictionInfo.name, [jurisdictionInfo]);

    // Derived labels for dynamic municipal naming
    // Priority: jurisdictionInfo (client-side, always up to date) > backend label > generic fallback
    const displayMfaLabel = useMemo(() => {
        if (jurName === 'Los Gatos') return 'MAGFA';
        return jurisdictionInfo?.mfaLabel || data.mfa_label || 'MFA';
    }, [data.mfa_label, jurisdictionInfo, jurName]);
    const displayMdaLabel = useMemo(() => jurisdictionInfo?.mdaLabel || data.mda_label || 'MDA', [data.mda_label, jurisdictionInfo]);

    // Fetch DEM Contours for the dedicated tab
    useEffect(() => {
        if (!data?.address || !data?.id) return;
        if (activeTab === 'map') return;

        // ⚡ Optimization: Only skip fetch if we already have the geometry (features) or encountered an error
        if (demContourTrace?.features || demContourError) {
            console.log("📊 Using pre-loaded contour geometry.");
            return;
        }

        const fetchContours = async () => {
            try {
                const response = await axios.get(`${API_BASE}/api/parcel-dem-contours`, {
                    params: {
                        address: data.address,
                        apn: data.apn,
                        city: jur,
                        analysis_id: data.id,
                        interval_ft: data.contour_interval_selected_ft || 2,
                        grid_spacing_ft: 8,
                        includeGeometry: true
                    }
                });
                const trace = response.data;
                if (trace?.success) {
                    setDemContourTrace(trace);
                } else {
                    setDemContourError(trace?.reason || "DEM contours unavailable");
                }
            } catch (err) {
                console.warn("DEM contour trace failed:", err);
                setDemContourError(err?.response?.data?.detail || err.message || "DEM contours unavailable");
            }
        };

        fetchContours();
    }, [activeTab, data.address, data.apn, data.id, jur, data.contour_details, data.contour_interval_selected_ft, demContourTrace?.features, demContourError]);
    const calcResult = useMemo(() => getZoningMetric(jur, slopeValue, acres, data.zone_code || "R1"), [jur, slopeValue, acres, data.zone_code]);
    const convMetric = calcResult?.convertedValue;

    // Early return AFTER hooks are defined to satisfy React rules
    if (!currentAnalysis) return null;

    // ── Constraints & Timeline from backend ─────────────────────────────────
    const constraints = data.development_constraints || {};
    const timeline = data.development_timeline || null;
    const setbacks = constraints.setbacks || {};
    const permitted = constraints.permitted_uses || data.permitted_uses || [];
    const aduEligible = constraints.adu_eligible ?? true;
    const jaduEligible = constraints.jadu_eligible ?? true;
    const duplexEleg = constraints.duplex_eligible ?? false;
    const heightFt = data.max_height_ft ?? constraints.height?.max_ft ?? null;
    const lotCovPct = constraints.lot_coverage_pct ?? null;
    const designRev = constraints.design_review_required || data.requires_design_review;

    // ── Helpers ─────────────────────────────────────────────────────────────
    const handleRefresh = () => dispatch(clearCurrentAnalysis());

    const executeExport = async () => {
        setIsGeneratingPDF(true);
        setShowMapWarningModal(false);
        try {
            toast.info("Preparing survey-standard report package...", { autoClose: 1800 });
            await exportAnalysisToPDF({
                ...data,
                mapAnalysisStats,
                acres,
                parcelFt2,
                existingMfaArea,
                existingMdaArea,
                plannedMfaArea,
                plannedMdaArea,
                customStructures,
                jurisdictionName: jurName,
                calcResult,
                convMetric,
                setbacks,
                permitted,
                aduEligible,
                jaduEligible,
                duplexEleg,
                heightFt,
                lotCovPct,
                designRev,
                timeline,
                slopeCat: slopeCat.text,
                demContourTrace,
                demContourError
            });

            toast.success("Feasibility Report downloaded successfully!");
        } catch (error) {
            console.error("Export failure:", error);
            toast.error(
                <div className="flex flex-col gap-1">
                    <span className="font-bold">Export Failed</span>
                    <span className="text-xs">There was an error generating your PDF. Please try again.</span>
                </div>
            );
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handleExport = async () => {
        // Notify user if LiDAR is still processing, but do not block export
        const isLiDARProcessing = demContourTrace?.status === "processing";

        if (isLiDARProcessing) {
            toast.info(
                <div className="flex flex-col gap-1">
                    <span className="font-bold">Partial Report</span>
                    <span className="text-xs">Topographic data is still processing; some terrain metrics may be incomplete.</span>
                </div>, { autoClose: 3500 }
            );
        }

        if (!canExport) {
            toast.info(
                <div className="flex flex-col gap-2">
                    <span className="font-bold">Pro Feature</span>
                    <span>Upgrade to export PDF reports.</span>
                    <button onClick={() => navigate("/#subscription-plans")}
                        className="mt-1 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50">
                        View Plans
                    </button>
                </div>, { autoClose: 5000 }
            );
            return;
        }

        const hasSnapshot = getStoredMapSnapshot(data);
        if (!hasSnapshot) {
            setShowMapWarningModal(true);
            return;
        }

        await executeExport();
    };

    const handleViewMap = () => {
        if (hasPro) {
            setActiveTab("map");
            // Scroll to the top of the tab container if needed
            window.scrollTo({ top: 300, behavior: "smooth" });
        } else {
            toast.info(
                <div className="flex flex-col gap-2">
                    <span className="font-bold">Pro Feature</span>
                    <span>Upgrade to view interactive maps.</span>
                    <button onClick={() => navigate("/#subscription-plans")}
                        className="mt-1 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50">
                        View Plans
                    </button>
                </div>, { autoClose: 5000 }
            );
        }
    };

    const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copied!"); };

    const formatDate = (ds) => {
        if (!ds) return "N/A";
        return new Date(ds).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    };

    const handleSaveArea = async () => {
        const areaVal = parseFloat(tempArea);
        if (isNaN(areaVal) || areaVal < 0) {
            toast.error("Please enter a valid square footage");
            return;
        }

        try {
            await dispatch(updateProposedScoping({
                analysisId: data.id,
                data: { living_area_ft2: areaVal }
            })).unwrap();
            setIsEditingArea(false);
            toast.success("Existing MFA updated! Potentials recalculated.");
        } catch (err) {
            toast.error("Failed to update: " + err);
        }
    };


    const tabContent = {
        overview: (
            <OverviewTab
                data={data}
                slopeValue={slopeValue}
                jurName={jurName}
                slopeCat={slopeCat}
                SlopeIcon={SlopeIcon}
                calcResult={calcResult}
                convMetric={convMetric}
                parcelFt2={parcelFt2}
                acres={acres}
                buildablePct={buildablePct}
                existingMfaArea={existingMfaArea}
                isEditingArea={isEditingArea}
                setIsEditingArea={setIsEditingArea}
                tempArea={tempArea}
                setTempArea={setTempArea}
                handleSaveArea={handleSaveArea}
                heightFt={heightFt}
                setbacks={setbacks}
                displayMfaLabel={displayMfaLabel}
                displayMdaLabel={displayMdaLabel}
                isDemPending={isDemPending}
            />


        ),
        terrain: (
            <TerrainTab
                mapAnalysisStats={mapAnalysisStats}
                data={data}
                jurName={jurName}
                handleViewMap={handleViewMap}
                hasPro={hasPro}
                existingMfa={existingMfaArea}
                existingMda={existingMdaArea}
                plannedMfa={plannedMfaArea}
                plannedMda={plannedMdaArea}
                displayMfaLabel={displayMfaLabel}
                displayMdaLabel={displayMdaLabel}
            />
        ),
        contour: (
            <ContourTab
                demContourTrace={demContourTrace}
                demContourError={demContourError}
                data={data}
                jurName={jurName}
                handleViewMap={handleViewMap}
            />
        ),
        zoning: (
            <ZoningTab
                data={data}
                jurName={jurName}
                hasPro={hasPro}
                heightFt={heightFt}
                lotCovPct={lotCovPct}
                setbacks={setbacks}
                permitted={permitted}
                aduEligible={aduEligible}
                jaduEligible={jaduEligible}
                duplexEleg={duplexEleg}
                constraints={constraints}
                displayMfaLabel={displayMfaLabel}
                displayMdaLabel={displayMdaLabel}
                isDemPending={isDemPending}
            />
        ),
        adu: (
            <ADUTab
                data={data}
                aduEligible={aduEligible}
                jaduEligible={jaduEligible}
                jurName={jurName}
                livingArea={existingMfaArea}
            />
        ),
        compliance: (
            <ComplianceTab
                data={data}
                slopeValue={slopeValue}
                designRev={designRev}
                hasPro={hasPro}
            />
        ),
        reports: (
            <ReportsTab
                data={data}
                jurName={jurName}
                timeline={timeline}
                hasPro={hasPro}
                canExport={canExport}
                handleExport={handleExport}
                formatDate={formatDate}
                setIsShareModalOpen={setIsShareModalOpen}
                handleViewMap={handleViewMap}
            />
        ),
        map: (
            <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-[750px] w-full rounded-[2rem] overflow-hidden relative border border-slate-200/60 shadow-2xl bg-slate-50"
            >
                <MapModal
                    isInline={true}
                    isOpen={activeTab === 'map'}
                    address={data.address}
                    apn={data.apn}
                    county={data.county}
                    sr_wkid={data.sr_wkid}
                    parcelAreaAcres={acres}
                    mda={data.mda_ft2}
                    mfa={data.mfa_ft2}
                    grossBuildingArea={data.gross_building_area_ft2}
                    livingArea={data.living_area_ft2}
                    avgSlope={slopeValue}
                    analysisId={data.id}
                    initialContourInterval={data.contour_interval_selected_ft}
                    initialContourTrace={demContourTrace}
                    onAnalysisComplete={handleMapAnalysisComplete}
                    onContoursGenerated={handleContoursGenerated}
                    onSnapshotsReady={() => {
                        if (pendingExport) {
                            setPendingExport(false);
                            executeExport();
                        }
                    }}
                />
            </motion.div>
        )
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 relative z-10"
        >
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2.5rem] shadow-2xl shadow-indigo-500/10 border border-white/40 overflow-hidden">

                {/* ── Premium Header ── */}
                <div className="relative bg-gradient-to-br from-slate-900 to-indigo-950 px-8 py-10 overflow-hidden">
                    {/* Abstract header glow */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2" />

                    <div className="relative z-10 flex justify-between items-start flex-wrap gap-6">
                        <div className="flex-1 min-w-0">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 }}
                                className="flex items-center gap-3 mb-3"
                            >
                                <div className="bg-indigo-500/20 backdrop-blur-md rounded-xl p-2 border border-white/10">
                                    <MapPin className="w-5 h-5 text-indigo-400" />
                                </div>
                                <span className="text-indigo-200 text-xs font-black uppercase tracking-[0.2em]">Development Feasibility Audit</span>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                {(() => {
                                    const parts = getAddressParts(data.address);
                                    if (parts.length <= 1) {
                                        return (
                                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-none tracking-tighter">
                                                {parts[0] || "Property Details"}
                                            </h2>
                                        );
                                    }
                                    return (
                                        <div className="flex flex-col gap-2">
                                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-none tracking-tighter">
                                                {parts[0]}
                                            </h2>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                {parts.slice(1).map((part, i) => (
                                                    <span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-indigo-200 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/10 leading-tight">
                                                        <span className="text-indigo-400">+</span> {part}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="flex items-center gap-4 mt-6 text-indigo-100/60 text-xs flex-wrap font-bold"
                            >
                                <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/5">
                                    <span className="opacity-60 text-[10px] uppercase tracking-widest">APN</span>
                                    <span className="text-indigo-100">{data.apn || "N/A"}</span>
                                    {data.apn && (
                                        <button onClick={() => copyToClipboard(data.apn)}
                                            className="hover:text-white transition-colors">
                                            <Copy className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        </div>

                        {/* Header actions */}
                        <div className="flex items-center gap-3">
                            <button onClick={() => setIsShareModalOpen(true)}
                                className="p-3.5 bg-white/5 hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10 active:scale-95" title="Share">
                                <Share2 className="w-5 h-5 text-white" />
                            </button>
                            <button onClick={handleRefresh}
                                className="px-6 py-3.5 bg-white hover:bg-slate-50 text-indigo-600 rounded-2xl transition-all shadow-xl shadow-indigo-500/10 active:scale-95 flex items-center gap-2 group font-black text-[11px] uppercase tracking-widest" title="New Search">
                                <Sparkles className="w-4 h-4" />
                                <span>New Search</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Tab bar — Floating interaction ── */}
                <div className="bg-white/30 px-8 py-2 border-b border-slate-200/60 transition-all duration-500">
                    <nav className="flex gap-2 overflow-x-auto scrollbar-hide py-2">
                        {TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        if (tab.id === "map" && !hasPro) {
                                            handleViewMap();
                                        } else {
                                            setActiveTab(tab.id);
                                        }
                                    }}
                                    className={`relative flex items-center gap-2.5 px-6 py-3 text-sm font-black tracking-tight transition-all duration-500 rounded-2xl ${isActive
                                        ? "text-indigo-700"
                                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-100/50"
                                        }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabPill"
                                            className="absolute inset-0 bg-white border border-slate-200/50 shadow-sm rounded-2xl"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                    <Icon className={`w-4 h-4 relative z-10 ${isActive ? "text-indigo-600" : "text-slate-400"}`} />
                                    <span className="relative z-10 uppercase text-[11px] tracking-widest">{tab.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* ── Tab content with reveal ── */}
                <div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                            {tabContent[activeTab]}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* ── Footer ── */}
                <div className="bg-slate-50/50 border-t border-slate-100 px-8 py-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span>Report generated by AI Zoning Engine</span>
                        </div>
                        <button
                            onClick={handleExport}
                            disabled={isGeneratingPDF}
                            className={`px-8 py-3 rounded-2xl transition-all font-black uppercase text-[10px] tracking-widest shadow-xl flex items-center gap-2 active:scale-95 ${isGeneratingPDF
                                    ? "bg-slate-700 text-slate-300 cursor-not-allowed"
                                    : "bg-slate-900 text-white hover:bg-slate-800 shadow-slate-900/10"
                                }`}
                        >
                            {isGeneratingPDF ? (
                                <>
                                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Download className="w-3.5 h-3.5" />
                                    Export PDF Analysis
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <SharePropertyModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)}
                propertyAddress={cleanAddress(data.address) || "Property Analysis"} propertyId={data.id} />

            {/* Map Warning Modal */}
            {showMapWarningModal && createPortal(
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative overflow-hidden"
                        >
                            <button
                                onClick={() => setShowMapWarningModal(false)}
                                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-20"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 blur-[40px] rounded-full translate-x-1/2 -translate-y-1/2" />
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                                    <Map className="w-8 h-8 text-amber-500" />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 mb-2">Map Snapshot Missing</h3>
                                <p className="text-sm text-slate-600 mb-8">
                                    You haven't viewed the map yet. Without a snapshot, the exported PDF will not include the <strong>Site Context Map</strong> page.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 w-full">
                                    <button
                                        onClick={() => {
                                            setShowMapWarningModal(false);
                                            setPendingExport(true);
                                            handleViewMap();
                                            toast.info("Export will automatically start once the map is fully loaded.");
                                        }}
                                        className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors shadow-lg shadow-indigo-600/20"
                                    >
                                        Go to Map
                                    </button>
                                    <button
                                        onClick={executeExport}
                                        className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-colors"
                                    >
                                        Export Anyway
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>,
                document.body
            )}
        </motion.div>
    );
};

export default AnalysisResults;
